import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription, BehaviorSubject, Observable, of } from 'rxjs';
import { QuoteAssistV2Service } from '../../../services/quote-assist-v2.service';
import { LoadingService } from '../../../services/loading.service';
import { IntegrationType, MixPanelEvents, QuoteStatus, QuoteAssistDemoVideos } from '../../../app/shared/enums';
import { ToastrService } from 'ngx-toastr';
import { 
  QuoteAssistV2Data, 
  RfqSource, 
  PartDetail, 
  QuoteDetails, 
  CostBreakdown,
  QuantityPricing,
  StepProgress,
  PartFile,
  PartConfiguration,
  MaterialItem,
  ToolItem,
  ProcessItem,
  AdditionalCostItem,
  CostItem
} from '../quote-assist-v2.model';
import {
  CostBreakdownChangeEvent,
  StepNavigationEvent,
  ProgressState,
  StepValidationState,
   StepConfig,
  SaveProgressEvent,
  QuoteSubmissionEvent,
  ErrorState,
  ComponentError,
  FileUploadEvent,
  PartsChangeEvent,
  BackendOperation,
  BackendOperationResult
} from '../models/component-events.model';
import { 
  MarketplaceDraftModel, 
  SaveMarketplaceDraftResponse,
  SaveQASupplierRfqQuoteDataV2Request
} from '../../../services/quote-assist-v2.service';

// Child Component Imports
import { ProgressTrackerComponent } from '../components/progress-tracker/progress-tracker.component';
import { QuoteDetailsFormComponent } from '../components/quote-details-form/quote-details-form.component';
import { PartsInformationComponent } from '../components/parts-information/parts-information.component';
import { CostBreakdownComponent } from '../components/cost-breakdown/cost-breakdown.component';
import { FileUploadModalComponent } from '../components/file-upload-modal/file-upload-modal.component';
import { LoadingOverlayComponent } from '../components/loading-overlay/loading-overlay.component';
import { tap, catchError } from 'rxjs/operators';
import { QuoteSuccessMessageComponent } from '../components/quote-success-message/quote-success-message.component';
import { QuoteAssistv2BaseComponent } from './quote-assist-v2-base.component';
import { CostBuilderDemoComponent } from '../components/cost-builder-demo/cost-builder-demo.component';
import { COST_BUILDER_DEMO_STEPS } from '../models/cost-builder-demo.model';
import { ProductAnalyticService } from '../../../services/product-analytic.service';

type PartIntelligenceMetadata = Pick<
  PartDetail,
  'risks' | 'riskCardData' | 'riskCardName' | 'riskCardDataRaw' | 'riskFeedbackAction'
>;

@Component({
  selector: 'app-quote-assist-v2',
  templateUrl: './quote-assist-v2.component.html',
  styleUrls: ['./quote-assist-v2.component.scss','./quote-assist-v2.disabled-overlay.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ProgressTrackerComponent,
    QuoteDetailsFormComponent,
    PartsInformationComponent,
    CostBreakdownComponent,
    FileUploadModalComponent,
    LoadingOverlayComponent,
    QuoteSuccessMessageComponent,
    CostBuilderDemoComponent
  ],
  standalone: true
})
export class QuoteAssistV2Component extends QuoteAssistv2BaseComponent implements OnInit, OnDestroy {
  // Core Data
  quoteData!: QuoteAssistV2Data;
  
  // Store draft V1 response data for property access
  private draftV1ResponseData: any = null;
  private parsedDraftV1MetaData: any = null;
  private parsedQuoteDetailsFromDynamo: any = null;
  
  // Store supplier metadata for process rates tracking
  private supplierMetaData: any = null;
  /** Parent-level source of truth for Part Intelligence keyed by partId. */
  private readonly partIntelligenceByPartId = new Map<number, PartIntelligenceMetadata>();
  
  // State Management
  private readonly _progressState$ = new BehaviorSubject<ProgressState>(this.initializeProgressState());
  readonly progressState$ = this._progressState$.asObservable();
  
  private readonly _validationState$ = new BehaviorSubject<StepValidationState>(this.initializeValidationState());
  readonly validationState$ = this._validationState$.asObservable();
  
  private readonly _errorState$ = new BehaviorSubject<ErrorState>(this.initializeErrorState());
  readonly errorState$ = this._errorState$.asObservable();
  
  // Component Configuration
  stepConfig: StepConfig = this.initializeStepConfig();
  
  // UI State
  isLoading = false;
  showFileUploadModal = false;
  currentFileUploadPartIndex?: number;
  validationErrors: { [key: string]: string } = {};
  currentPartIndex: number = 0;
  partsPricingData: PartDetail[] = [];
  rfqMetaDataSUW: any = null;
  private hasRealData = false; // Flag to track if we have real data (not just initialized)
  
  // Shared state for edit mode
  isEditMode: boolean = false;
  quoteDownloadedOrMailSentSuccess=false
  // MFG integration flag for parts-information component
  skipToConfigurationForMFG: boolean = false;
  isViewMode: boolean = false;
  isCostBuilderDemoActive = false;
  costBuilderDemoStartIndex = 0;
  readonly costBuilderDemoSteps = COST_BUILDER_DEMO_STEPS;
  // Query Parameters
  queryParams: {
    source?: RfqSource;
    rfqId?: string;
    integration?: RfqSource;
    companyId?: number;
    contactId?: number;
    rfqQuoteStatus?: string;
    surveyDone?: boolean;
    emailId?: string;
  } = {};
  
  // Subscriptions
  private subscriptions: Subscription[] = [];
  
  // ViewChild References
  @ViewChild(ProgressTrackerComponent) progressTracker!: ProgressTrackerComponent;
  @ViewChild(QuoteDetailsFormComponent) quoteDetailsForm!: QuoteDetailsFormComponent;
  @ViewChild(PartsInformationComponent) partsInformation!: PartsInformationComponent;
  @ViewChild(CostBreakdownComponent) costBreakdown!: CostBreakdownComponent;
  @ViewChild(FileUploadModalComponent) fileUploadModal!: FileUploadModalComponent;
  
  // Backend Operation Configuration
  private readonly stepBackendOperations: BackendOperation[] = [
    {
      stepId: 'details',
      apiCall: (data) => {
        if(this.getIntegrationType() !== 'MFG') {
          return this.quoteAssistV2Service.saveQuoteDetails(data)
        } 
        else {
          return of({
            status: true,
            data: {
              companyId: this.queryParams.companyId,
              supplierId: this.queryParams.contactId,
              rfqId: this.queryParams.rfqId,
              quoteStatus: 'Draft',
              rfqPartsMetaData: JSON.stringify(data),
              createdOn: new Date().toISOString(),
              source: 'MFG'
            },
            message: 'MFG integration',
            errors: []
          });
        }
      },
      required: true,
      preNavigation: true,
      dataTransformer: (data) => this.quoteAssistV2Service.createFormDataFromQuoteDetails(
        data.quoteDetails,
        this.queryParams.companyId || 0,
        this.queryParams.integration || 'MFG',
        this.rfqMetaDataSUW
      )
    },
    {
      stepId: 'parts-information',
      apiCall: (data) => {
        if(this.getIntegrationType() !== 'MFG') {
          return this.quoteAssistV2Service.draftDirectRFQQuoteData(
            data,
            this.queryParams.companyId || 0,
            this.queryParams.contactId || 0,
            this.queryParams.integration || 'MFG'
          );
        } else {
          return this.quoteAssistV2Service.draftMarketplaceRFQQuoteDataV2(this.transformToMarketplaceFormatV2(this.queryParams.companyId || 0, QuoteStatus.Draft));
        }
      },
      required: false,
      preNavigation: false
    },
    {
      stepId: 'costs',
      apiCall: (data) => {
        if(this.getIntegrationType() !== 'MFG') {
          return this.quoteAssistV2Service.draftDirectRFQQuoteData(
            data,
            this.queryParams.companyId || 0,
            this.queryParams.contactId || 0,
            this.queryParams.integration || 'MFG'
          );
        } else {
          return this.quoteAssistV2Service.draftMarketplaceRFQQuoteDataV2(this.transformToMarketplaceFormatV2(this.queryParams.companyId || 0, QuoteStatus.Draft));
        }
      },
      required: false,
      preNavigation: false
    }
  ];
  
  constructor(
    private route: ActivatedRoute,
    private quoteAssistV2Service: QuoteAssistV2Service,
    public loadingService: LoadingService,
    private cdr: ChangeDetectorRef,
    private toastrService: ToastrService,
    private productAnalyticService: ProductAnalyticService
  ) { super();}
  
  ngOnInit(): void {
    this.initializeComponent();
    
    // Subscribe to loading service
    this.subscriptions.push(
      this.loadingService.loadingState$.subscribe(loadingState => {
        // Update local loading state based on service
        if (loadingState.isLoading && loadingState.loadingType === 'full-page') {
          this.isLoading = true;
        } else if (!loadingState.isLoading) {
          this.isLoading = false;
        }
      })
    );

    // Expose debug methods for testing
    this.exposeDebugMethods();
  }
  
  ngOnDestroy(): void {
    // Save progress before destroying component
    if (this.quoteData) {
      this.saveProgress();
    }
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
  
  // ===== INITIALIZATION =====
  
  private initializeComponent(): void {
    this.route.queryParams.subscribe(params => {
      this.queryParams = {
        source: params['source'] as RfqSource,
        rfqId: params['rfqId'],
        integration: params['integration'] as RfqSource,
        companyId: params['companyId'] ? parseInt(params['companyId']) : undefined,
        contactId: params['contactId'] ? parseInt(params['contactId']) : undefined,
        rfqQuoteStatus: params['rfqQuoteStatus'],
        surveyDone: params['surveyDone'] === 'true',
        emailId: params['emailId'] || '',
      };
      
      // Map emailId to email for Mixpanel tracking
      if (this.queryParams.emailId) {
        (this.queryParams as any).email = this.queryParams.emailId;
      }
      // Initialize quoteData after query params are processed
      this.quoteData = this.initializeQuoteDataWithDefaultValue();
      // Trigger initial change detection
      this.cdr.detectChanges();
      
      if (this.queryParams.rfqId) {
        this.loadDataByIntegration();
      } else {
        this.initializeNewQuote();
      }
    });
  }



  private loadDataByIntegration(): void {
    this.isLoading = true;
    // First try to load from IndexedDB (Tier 1)
    this.quoteAssistV2Service.getQuoteAssistV2Progress(this.queryParams.rfqId!)
      .subscribe({
        next: (indexedDBData: QuoteAssistV2Data | null) => {
          this.configureStepValidation();
          this.updateProgressState();
          if (indexedDBData) {
            const isDataComplete = this.isIndexedDBDataComplete(indexedDBData);
            
            if (isDataComplete) {              
              this.quoteData = indexedDBData;
              this.hasRealData = true;

              if(this.queryParams.integration===IntegrationType.MFG){
                if(["WON","LOST","CLOSED"].indexOf(this.queryParams.rfqQuoteStatus || '')>-1){
                  this.isViewMode = true;
                }
              }else{
                if(this.queryParams.rfqQuoteStatus === QuoteStatus.Quoted){
                  this.isViewMode = true;
                }
              }
              
              // Set skipToConfigurationForMFG flag when data is loaded from IndexedDB and integration is MFG
              if (this.getIntegrationType() === 'MFG') {
                this.skipToConfigurationForMFG = true;
                //console.log('[Quote-Assist-V2] MFG integration with IndexedDB data: Setting skipToConfigurationForMFG = true');
              }
              
              this.restoreProgressFromQuoteData();
              this.isLoading = false;
              // Trigger change detection for OnPush strategy
              this.cdr.detectChanges();
            } else {
              // ❌ Incomplete data in IndexedDB - try draft data (Tier 2) or fresh data (Tier 3)
              this.handleNoIndexedDBData();
            }
          } else {
            // ❌ No IndexedDB data - try draft data (Tier 2) or fresh data (Tier 3)
            this.handleNoIndexedDBData();
          }
        },
        error: (error) => {
          console.error('Error loading from IndexedDB:', error);
          // ❌ IndexedDB error - try draft data (Tier 2) or fresh data (Tier 3)
          this.handleNoIndexedDBData();
        }
      });
  }

  /**
   * Handle the case when no IndexedDB data is found
   * Implements the same three-tier strategy as V1
   */
  private handleNoIndexedDBData(): void {
    const integration = this.queryParams.integration;
    const companyId = this.queryParams.companyId || 0;
    const rfqId = this.queryParams.rfqId!;

    if(this.queryParams.integration===IntegrationType.MFG){
      if(["WON","LOST","CLOSED"].indexOf(this.queryParams.rfqQuoteStatus || '')>-1){
        this.isViewMode = true;
      }
    }else{
      if(this.queryParams.rfqQuoteStatus === QuoteStatus.Submitted){
        this.isViewMode = true;
      }
    }

    if (!integration) {
      this.handleError('Integration Type Missing', new Error('Integration type is required'), 'Please provide integration type');
      this.initializeNewQuote();
      return;
    }
    switch (integration.toUpperCase()) {
      case 'MFG':
        // For MFG integration, try draft data first (Tier 2), then fresh metadata (Tier 3)
        this.logEvent('No IndexedDB data for MFG, trying draft data', { rfqId: this.queryParams.rfqId });
        this.fetchMetaDataFromAPIV2(companyId, rfqId);
        break;
      case 'MFGDIRECT': 
        this.fetchDirectIntegrationData(companyId, integration);
        break;
      case 'SUW':
        this.loadWebsiteRfqData(rfqId, companyId,integration);
        break;
      default:
        this.handleError('Invalid Integration Type', new Error(`Unknown integration type: ${integration}`), 'Invalid integration type provided');
        this.initializeNewQuote();
    }
  }

  /**
   * Fetch MFG integration data (Marketplace RFQs)
   */
  private fetchMfgIntegrationData(companyId: number): void {
    this.loadingService.showActionLoading('mfg data fetch', 'Loading MFG data...', 'full-page');
    
    this.quoteAssistV2Service.getMfgIntegrationData(this.queryParams.rfqId!, companyId)
      .subscribe({
        next: (response) => {
          this.loadingService.hideLoading();
          if (response.status && response.data) {
            try {
              this.quoteData = this.quoteAssistV2Service.transformMarketplaceData(response);
              this.configureStepValidation();
              this.saveProgress();
              this.updateProgressState();
              this.isLoading = false;
              // Trigger change detection for OnPush strategy
              this.cdr.detectChanges();
              this.logEvent('MFG data loaded successfully', { rfqId: this.queryParams.rfqId });
            } catch (error) {
              this.handleError('Data Transformation Error', error as Error, 'Failed to process MFG data');
              this.initializeNewQuote();
            }
          } else {
            this.handleError('MFG Data Error', new Error(response.message || 'No data received'), 'Failed to load MFG data');
            this.initializeNewQuote();
          }
        },
        error: (error) => {
          this.loadingService.hideLoading();
          this.handleError('MFG API Error', error, 'Failed to fetch MFG data from server');
          this.initializeNewQuote();
        }
      });
  }

  /**
   * Fetch Direct integration data (Direct RFQs)
   */
  private fetchDirectIntegrationData(companyId: number, integration: RfqSource,websiteRFQData:any=null): void {
    this.loadingService.showActionLoading('direct data fetch', 'Loading direct data...', 'full-page');
    
    this.quoteAssistV2Service.getDirectIntegrationData(this.queryParams.rfqId!, companyId, integration)
      .subscribe({
        next: (response) => {
          this.loadingService.hideLoading();
          if (response.status && response.data) {
            
              this.quoteData = this.quoteAssistV2Service.transformDirectData(response,integration,websiteRFQData);
              this._progressState$.value.steps = this.quoteData.stepProgress || [];
              this.restoreProgressFromQuoteData();
              this.saveProgress();
              this.isLoading = false;
              // Trigger change detection for OnPush strategy
              this.cdr.detectChanges();
              this.logEvent('Direct data loaded successfully', { rfqId: this.queryParams.rfqId, integration });
          } else {
            // For MFGDirect, it's okay to have no data - start with blank form     
            this.logEvent('MFGDirect - No data found, starting fresh', { rfqId: this.queryParams.rfqId });
            this.initializeNewQuote();
          }
        },
        error: (error) => {
          this.loadingService.hideLoading();
          // For MFGDirect, it's okay to have API errors - start with blank form
          if (integration.toUpperCase() === 'MFGDIRECT') {
            this.initializeNewQuote();
            this.logEvent('MFGDirect - API error, starting fresh', { rfqId: this.queryParams.rfqId, error: error.message });
          } else {
            this.handleError('Direct API Error', error, 'Failed to fetch direct data from server');
            this.initializeNewQuote();
          }
        }
      });
  }

  fetchMetaDataFromAPIV2(companyId: number, rfqId: string): void {
    this.isLoading = true;
    this.loadingService.showActionLoading('metadata fetch', 'Loading metadata...', 'full-page');
    
    try {
      this.quoteAssistV2Service.getMetadataRev(rfqId, this.queryParams.companyId!).subscribe({
        next: (apiResponse: any) => {
            try {
              this.parsedQuoteDetailsFromDynamo = apiResponse.data;
              this.quoteAssistV2Service.fetchDraftV1Style(companyId, rfqId).subscribe({
                next: (response) => {
                  this.parseDraftV1MetaData(response);
                  
                  this.configureStepValidation();
                  this.resetValidationState();
                  
                  this.cdr.detectChanges();
                },
                error: (error) => {
                  console.error('Error fetching draft V1 style:', error);
                  
                  // Even if draft V1 fails, still parse the API response
                  this.quoteData = this.parseApiResponseV2(apiResponse);
                  this.hasRealData = true;
                }
              });
            
          } catch (error) {
            this.handleRfqFetchError(error);
          }
        },
        error: (error) => {
          this.handleRfqFetchError(error);
        },
        complete: () => {
          this.isLoading = false;
          this.loadingService.hideLoading();
        }
      });
    } catch (error) {
      this.handleRfqFetchError(error);
      this.isLoading = false;
      this.loadingService.hideLoading();
    }
  }
  loadWebsiteRfqData(rfqId: string, companyId: number,integration:RfqSource): void {
    this.isLoading = true;
    this.loadingService.showActionLoading('direct metadata fetch', 'Loading direct metadata...', 'full-page');
    this.quoteAssistV2Service.getMetadata(rfqId, companyId).subscribe({
      next: (response: any) => {
        this.fetchDirectIntegrationData(companyId, integration,response.data);
      },
      error: (error) => {
        this.handleRfqFetchError(error);
        this.isLoading = false;
        this.loadingService.hideLoading();
      },
      complete: () => {
        this.isLoading = false;
        this.loadingService.hideLoading();
      }
    });
  }

  /**
   * Parse API response using V1-style logic but transform to V2 data structure
   * Reuses V1's parseApiResponse logic but adapts for QuoteAssistV2Data format
   */
  private parseApiResponseV2(response: any): QuoteAssistV2Data {
    try {
      let parsedMetaData = {};
      try {
        parsedMetaData = JSON.parse(response.data.rfqMetaData);
      } catch (error) {
        throw new Error("Error during the parsing of RFQ metadata.");
      }

      if (!parsedMetaData || Object.keys(parsedMetaData).length === 0) {
        throw new Error("No data keys could be found after the parsing of RFQ metadata.");
      }

      // Convert to camelCase (reusing V1's toCamelCase logic)
      const parseList = this.toCamelCase(parsedMetaData) as Partial<any[]>;
      const rfqPartList: any[] = parseList.map(obj => ({ ...this.getDefaultRfqPart(), ...obj }));

      // Parse supplier metadata
      let parsedSupplierMetaData = {};
      try {
        parsedSupplierMetaData = JSON.parse(response.data.supplierMetaData);
        // Convert to camelCase and store
        this.supplierMetaData = this.toCamelCase(parsedSupplierMetaData);
        // Initialize processRates array if it doesn't exist
        if (!this.supplierMetaData.processRates || !Array.isArray(this.supplierMetaData.processRates)) {
          this.supplierMetaData.processRates = [];
        }
      } catch (error) {
        console.error('Error parsing supplier metadata:', error);
        // Initialize empty supplier metadata structure
        this.supplierMetaData = {
          companyName: '',
          employeeSize: '',
          myCapabilities: [],
          myEquipment: [],
          industryServing: [],
          myAddress: '',
          zipCode: '',
          processRates: [],
          additionalInfo: []
        };
      }
 
      // Transform to V2 QuoteDetails format
      const quoteDetails: QuoteDetails = {
        rfqId: response.data.rfqId.toString(),
        rfqName: response.data.rfqName || '',
        companyName: response.data.buyerCompanyName || '',
        companyEmail: response.data.buyerCompanyEmail || '',
        companyPhone: response.data.buyerCompanyPhone || '',
        address: this.formatBuyerCompanyAddress(response.data),
        city: response.data.buyerCompanyCity || '',
        state: response.data.buyerCompanyState || '',
        zipCode: response.data.buyerCompanyZipCode || '',
        quoteNumber: response.data.quoteNumber || '',
        quoteExpiryDate: response.data.quoteExpiryDate || '',
        paymentTerm: response.data.paymentTerm || '',
        companyId: response.data.companyId,
        contactId: this.queryParams.contactId || 0
      };
      
      // Transform parts to V2 PartDetail format
      const partDetails: PartDetail[] = rfqPartList.map((part: any, index: number) => ({
        partId: part.rfqPartId,
        partName: part.partName || '',
        partNumber: part.rfqPartId?.toString() || index.toString(),
        files: (part.pdfFiles || []).map((file: any) => ({
          fileName: file.fileName,
          fileType: this.getFileType(file.fileUrl?.split('/').pop() || ''),
          fileURL: file.fileUrl || '',
          extractedContent: file.extractedContent || ''
        })),
        notesForBuyer: part.notesForBuyer || '',
        quantityPricing: this.transformQuantityPricing(part),
        configuration: {
          materials: [],
          tools: [],
          processes: [],
          additionalCosts: []
        },
        isActivePart: index === 0 // Set the first part as active by default
      }));
      
      // Initialize cost breakdown with default values
      const costBreakdown: CostBreakdown = {
        materials: { totalCost: 0, items: [] },
        tooling: { totalCost: 0, items: [] },
        manufacturing: { totalCost: 0, items: [] },
        labor: { totalCost: 0, items: [] },
        overhead: { totalCost: 0, items: [] },
        otherCosts: { totalCost: 0, items: [] },
        profit: { profitMargin: 15, totalProfit: 0 }
      };

      return {
        rfqId: response.data.rfqId.toString(),
        sourceType: this.queryParams.integration || 'MFG',
        lastUpdated: new Date().toISOString(),
        quoteDetails,
        partDetails,
        costBreakdown,
        // Initialize progress state for new data
        currentStep: 'details',
        currentPartIndex: 0,
        stepProgress: this.getSteps(),
        overallProgress: 0,
        lastActiveTimestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Unexpected error while parsing API response:', error);
      throw new Error("An unexpected error occurred while processing the RFQ data.");
    }
  }

  /**
   * Transform quantity pricing from V1 format to V2 format
   * Always returns exactly 3 rows with proper editing permissions
   */
  private transformQuantityPricing(part: any): QuantityPricing[] {
    const quantities = [part.qty1, part.qty2, part.qty3];
    
    // Always return exactly 3 rows
    return quantities.map((qty, index) => {
      const isValidQuantity = qty && qty > 0;
      return {
        quantity: qty || null,
        leadtimeValue: undefined,
        leadtimeUnit: 'Days' as const,
        unitCost: 0,
        markup: null,
        unitPrice: 0,
        total: 0,
        order: index + 1,
        unitPriceTotal: 0,
        isLeadtimeEditing: qty > 0 ? true : false,
        isQuantityEditable: isValidQuantity, // Only editable if quantity is valid
        isValidQuantity: isValidQuantity,    // Valid if quantity exists and > 0
        showLeadTimeInputFields: false       // Default to not showing input fields
      };
    });
  }

  /**
   * Get default RFQ part structure for merging with parsed data
   */
  private getDefaultRfqPart(): any {
    return {
      rfqPartId: 0,
      partName: '',
      notesForBuyer: '',
      process: '',
      technique: '',
      material: '',
      postProduction: '',
      tolerance: '',
      width: 0.00,
      height: 0.00,
      depth: 0.00,
      length: '',
      diameter: '',
      geometry: '',
      dimensionUnit: '',
      rfqNda: '',
      rfqLocation: '',
      qty1: 0,
      qty2: 0,
      qty3: 0,
      pdfFiles: []
    };
  }

  /**
   * Convert object keys to camelCase (reused from V1)
   */
  private toCamelCase(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(item => this.toCamelCase(item));
    } else if (obj && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc, key) => {
        const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        acc[camelKey] = this.toCamelCase(obj[key]);
        return acc;
      }, {} as any);
    }
    return obj;
  }

  /**
   * Handle RFQ fetch errors (reused from V1 pattern)
   */
  private handleRfqFetchError(error: any): void {
    this.isLoading = false;
    this.loadingService.hideLoading();
    this.handleError('RFQ Fetch Error', error, 'Failed to fetch data for the RFQ');
    console.error('Error during metadata fetching:', error);
  }

  /**
   * Reset validation state for all steps
   */
  private resetValidationState(): void {
    const currentValidation = this._validationState$.value;
    const resetValidation: StepValidationState = {
      details: { ...currentValidation.details, isValid: false },
      'parts-information': { ...currentValidation['parts-information'], isValid: false },
      costs: { ...currentValidation.costs, isValid: false }
    };
    this._validationState$.next(resetValidation);
  }

  private initializeNewQuote(): void {
    // Ensure quoteData is properly initialized with current query params
    if (!this.quoteData || !this.quoteData.quoteDetails.companyId) {
      this.quoteData = this.initializeQuoteDataWithDefaultValue();
    }
    
    this.configureStepValidation();
    this.updateProgressState();
    this.isLoading = false;
    
    // Trigger change detection for OnPush strategy
    this.cdr.detectChanges();
  }
  
  onQuoteDetailsSaveSuccess(response: any): void {
    // Handle successful save of quote details (legacy method - navigation now handled through onStepNavigation)
    this.logEvent('Quote Details Save Success', response);
    
    // Update the quote data with the response by creating a new object reference
    if (response.data?.rfqId) {
      // Preserve the isQuoteComplete flag when updating quote details
      const currentIsQuoteComplete = this.quoteData.quoteDetails?.isQuoteDetailsCompleted;
      this.quoteData.quoteDetails = {
        ...this.quoteData.quoteDetails,
        rfqId: response.data.rfqId,
        isQuoteDetailsCompleted: currentIsQuoteComplete || true // Preserve the flag or set to true for details step
      };
    }
    
    // Save progress after successful API call
    this.saveProgress();
    
    // Note: Navigation is now handled through onStepNavigation method
    // This method is kept for backward compatibility
  }

  onQuoteDetailsSaveError(error: any): void {
    // Handle save error
    this.handleError('Quote Details Save', new Error(error.error), error.error || 'Failed to save quote details');
  }

  onRfqNameChange(value: string): void {
    this.quoteData.quoteDetails.rfqName = value;
    this.saveProgress();
    this.logEvent('RFQ Name Changed', { value });
  }

  /**
   * Handle RFQ name validation trigger from quote details form
   * This is called when validation fails and we need to show RFQ name error styling
   */
  onTriggerRfqNameValidation(): void {
    // Trigger RFQ name validation in the progress tracker to show error styling
    if (this.progressTracker) {
      this.progressTracker.triggerRfqNameValidation();
    }
    
    // Also trigger validation in the quote details form to show field errors
    if (this.quoteDetailsForm) {
      this.quoteDetailsForm.triggerValidation();
    }
  }

  onPartsInformationChange(event: PartsChangeEvent): void {
    // Update the parts data
    if(this.getIntegrationType() === 'MFGDirect'){
      this.quoteData.partDetails = this.mergePartIntelligenceMetadata(event.parts);
      
      // Ensure proper isActivePart management when parts are added/changed
      if (this.quoteData.partDetails.length > 0) {
        // If this is a new part being added (parts array length increased)
        if (this.quoteData.partDetails.length > this.currentPartIndex + 1) {
          // Set the newly added part as active
          this.quoteData.partDetails.forEach((part, index) => {
            part.isActivePart = index === this.quoteData.partDetails.length - 1;
          });
          // Update current part index to the newly added part
          this.currentPartIndex = this.quoteData.partDetails.length - 1;
        } else if (this.currentPartIndex >= 0 && this.currentPartIndex < this.quoteData.partDetails.length) {
          // Ensure the current part is active
          this.quoteData.partDetails.forEach((part, index) => {
            part.isActivePart = index === this.currentPartIndex;
          });
        }
      }
      
      this.updateValidationState('parts-information', event);
      
      // Update main validation errors object for progress-tracker
      // First, remove all part-related validation errors from parts-information
      Object.keys(this.validationErrors).forEach(key => {
        if (key.match(/^part\d+_(partName|partNumber|quantity|total|unitPrice|unitPriceTotal|leadtime)$/)) {
          delete this.validationErrors[key];
        }
      });
      
      // Then add the current validation errors
      this.validationErrors = { ...this.validationErrors, ...event.errors };
      
      this.saveProgress();
      this.updateProgressState();
      
      // Trigger change detection for OnPush strategy
      this.cdr.detectChanges();
      
      if (event.stepCompleted) {
        this.logEvent('Parts Information Completed', { isValid: event.isValid, errors: event.errors });
        // Update supplier metadata with process rates when part configuration is completed
        this.updateSupplierMetadata();
      }
    }
  }

  /**
   * Handle draft state changes from Parts Information component
   * Updates isDraftEnabled based on configuration view condition
   * Once enabled, stays enabled (sticky behavior)
   */
  onDraftStateChange(enableDraft: boolean): void {
    // Once enabled, keep it enabled (one-way toggle)
    if (enableDraft && !this.quoteData.quoteDetails.isDraftEnabled) {
      this.quoteData.quoteDetails.isDraftEnabled = true;
      this.cdr.detectChanges();
    }
  }
  

  
  onCostBreakdownChange(event: CostBreakdownChangeEvent): void {
    this.quoteData.costBreakdown = event.costBreakdown;
    this.updateValidationState('costs', event);
    this.saveProgress();
    this.updateProgressState();
    
    // Trigger change detection for OnPush strategy
    this.cdr.detectChanges();
    
    if (event.stepCompleted) {
      this.logEvent('Cost Breakdown Completed', { isValid: event.isValid, errors: event.errors });
    }
  }

  onWatchDemoRequested(): void {
    // Track Watch Demo button click
    const progressState = (this.progressState$ as any).value;
    this.productAnalyticService.mixpanelTracking(
      QuoteAssistDemoVideos.WatchDemoButtonClicked,
      this.queryParams,
      {
        action: 'watch_demo_opened',
        component: 'parts-information',
        section: 'cost-builder'
      },
      {
        currentStep: progressState?.currentStep || 'parts-information',
        currentPartIndex: this.currentPartIndex,
        totalParts: this.quoteData?.partDetails?.length || 0
      }
    );

    this.isCostBuilderDemoActive = true;
    this.costBuilderDemoStartIndex = 0;
    this.cdr.markForCheck();
  }

  onCostBuilderDemoClosed(): void {
    this.isCostBuilderDemoActive = false;
    this.costBuilderDemoStartIndex = 0;
    this.cdr.markForCheck();
  }

  onCostBuilderDemoCompleted(): void {
    // Track Done button click
    const progressState = (this.progressState$ as any).value;
    this.productAnalyticService.mixpanelTracking(
      QuoteAssistDemoVideos.QuoteAssistDemoDone,
      this.queryParams,
      {
        action: 'demo_completed',
        totalSteps: this.costBuilderDemoSteps.length,
        completedAllSteps: true,
        finalStepIndex: this.costBuilderDemoStartIndex,
        finalStepNumber: this.costBuilderDemoStartIndex + 1
      },
      {
        currentStep: progressState?.currentStep || 'parts-information',
        currentPartIndex: this.currentPartIndex,
        totalParts: this.quoteData?.partDetails?.length || 0
      }
    );
    
    this.onCostBuilderDemoClosed();
  }

  onCostBuilderDemoStepChange(index: number): void {
    // Map index (0-4) to step number (1-5)
    const stepNumber = index + 1;
    
    // Get step event name
    const stepEventName = this.getStepEventName(stepNumber);
    
    // Track step view
    const progressState = (this.progressState$ as any).value;
    const currentStepData = this.costBuilderDemoSteps[index];
    
    this.productAnalyticService.mixpanelTracking(
      stepEventName,
      this.queryParams,
      {
        action: 'demo_step_viewed',
        stepNumber: stepNumber,
        stepIndex: index,
        stepId: currentStepData?.id || '',
        stepTitle: currentStepData?.title || '',
        totalSteps: this.costBuilderDemoSteps.length
      },
      {
        currentStep: progressState?.currentStep || 'parts-information',
        currentPartIndex: this.currentPartIndex,
        demoStartIndex: this.costBuilderDemoStartIndex
      }
    );
    
    this.costBuilderDemoStartIndex = index;
    this.cdr.markForCheck();
  }

  /**
   * Helper method to get step event name
   */
  private getStepEventName(stepNumber: number): string {
    const stepEvents: { [key: number]: QuoteAssistDemoVideos } = {
      1: QuoteAssistDemoVideos.QuoteAssistDemoStep1,
      2: QuoteAssistDemoVideos.QuoteAssistDemoStep2,
      3: QuoteAssistDemoVideos.QuoteAssistDemoStep3,
      4: QuoteAssistDemoVideos.QuoteAssistDemoStep4,
      5: QuoteAssistDemoVideos.QuoteAssistDemoStep5
    };
    return stepEvents[stepNumber] || QuoteAssistDemoVideos.QuoteAssistDemoStep1;
  }

  onPartEdit(event: { partIndex: number }): void {
    // if(this.getIntegrationType() === 'MFGDirect'){
      this.currentPartIndex = event.partIndex;
      this.isEditMode = true;    
      
      // Update isActivePart for all parts when editing a specific part
      if (this.quoteData && this.quoteData.partDetails) {
        this.quoteData.partDetails = this.quoteData.partDetails.map((part, index) => ({
          ...part,
          isActivePart: index === event.partIndex
        }));
        
        // Force change detection to ensure UI updates
        this.cdr.detectChanges();
      }
      
      this.navigateToStep('parts-information');

      if (this.partsInformation) {
        this.partsInformation.switchToPart(event.partIndex, 'configuration');
      }
      this.logEvent('Part Edit Requested', { partIndex: event.partIndex });
    // }
  }
  
  onStepNavigation(event: StepNavigationEvent): void {
    if (event.canProceed) {
      // Store MFG flag for parts-information component
      if (event.skipToConfiguration) {
        this.skipToConfigurationForMFG = true;
      }
      // this.updateProgressState();
      
      // Handle response data if coming from details step
      if (event.fromStep === 'details' && event.response?.data?.rfqId) {
        // Preserve the isQuoteDetailsCompleted flag when updating quote details
        const currentIsQuoteComplete = this.quoteData.quoteDetails?.isQuoteDetailsCompleted;
        this.quoteData.quoteDetails = {
          ...this.quoteData.quoteDetails,
          rfqId: event.response.data.rfqId,
          isQuoteDetailsCompleted: currentIsQuoteComplete || true // Preserve the flag or set to true for details step
        };
        this.navigateToStep('parts-information');
        this.logEvent('Quote Details Save Success', event.response);
      }
      
      // Handle parts pricing data if coming from parts-information step
      if (event.fromStep === 'parts-information' && event.partsPricingData) {
        this.partsPricingData = event.partsPricingData;
        this.quoteData.partDetails = this.mergePartIntelligenceMetadata(event.partsPricingData);
        
        if (event.isConfirmQuote) {
          this.quoteData.isConfirmQuote = true;
        }
        this.submitQuote('draft', false);
        this.resetEditMode();
        
        this.logEvent('Parts Pricing Data Received', event.partsPricingData);
      }
      
      if (event.backendData?.quoteDetails) {
        this.quoteData.quoteDetails = {
          ...this.quoteData.quoteDetails,
          ...event.backendData.quoteDetails
        };
      }
      
      // Set details step as valid
      const currentValidationState = this._validationState$.value;
      const updatedValidationState = {
        ...currentValidationState,
        details: {
          ...currentValidationState.details,
          isValid: true,
          errors: {},
          warnings: {},
          touched: {}
        }
      };
      this._validationState$.next(updatedValidationState);
      
      // Handle backend operations
      if (event.requiresBackendCall && event.backendData) {
        this.loadingService.showActionLoading('backend operation', 'Saving data...', 'full-page');
        
        this.handleStepBackendOperation(event.fromStep, event.backendData, true)
          .subscribe({
            next: (result) => {
              this.loadingService.hideLoading();
              if (result.success) {
                this.updateDataWithBackendResponse(event.fromStep, result.data);
                
                // Notify child component of completion
                if (event.fromStep === 'details' && this.quoteDetailsForm) {
                  this.quoteDetailsForm.onBackendOperationComplete(true, result.data);
                }
                
                this.saveData().subscribe({
                  next: () => {
                    this.navigateToStep(event.toStep);
                    this.triggerUserGuidingOnProceedToPartQuoting(event);
                  },
                  error: (error) => {
                    this.handleNavigationError(error);
                  }
                });
              } else {
                // Notify child component of failure
                if (event.fromStep === 'details' && this.quoteDetailsForm) {
                  this.quoteDetailsForm.onBackendOperationComplete(false);
                }
                this.handleNavigationError(new Error(result.error));
              }
            },
            error: (error) => {
              // Notify child component of failure
              if (event.fromStep === 'details' && this.quoteDetailsForm) {
                this.quoteDetailsForm.onBackendOperationComplete(false);
              }
              this.handleNavigationError(error);
            }
          });
      } else {
        // No backend call needed, proceed directly
        this.saveData().subscribe({
          next: () => {
            this.navigateToStep(event.toStep);
            this.triggerUserGuidingOnProceedToPartQuoting(event);
          },
          error: (error) => {
            this.handleNavigationError(error);
          }
        });
      }
    } else {
      // Handle validation errors
      this.handleValidationErrors(event.validationErrors);
    }
  }

  private triggerUserGuidingOnProceedToPartQuoting(event: StepNavigationEvent): void {
    if (event.fromStep !== 'details' || event.toStep !== 'parts-information') {
      return;
    }

    if (!this.progressTracker) {
      return;
    }

    // Wait a tick so the parts step/progress tracker state is settled.
    setTimeout(() => {
      this.progressTracker?.triggerUserGuidingInitialization();
    }, 0);
  }

  /**
   * Preserve intelligence metadata while replacing parts from child events.
   * Child components can emit cloned parts that omit these transient fields.
   */
  private mergePartIntelligenceMetadata(incomingParts: PartDetail[]): PartDetail[] {
    const existingParts = this.quoteData?.partDetails || [];
    this.upsertPartIntelligenceStore(existingParts);

    const existingByPartId = new Map<number, PartDetail>();
    existingParts.forEach((existingPart) => {
      if (existingPart?.partId != null) {
        existingByPartId.set(existingPart.partId, existingPart);
      }
    });

    return incomingParts.map((incomingPart, index) => {
      const existingPartById =
        incomingPart?.partId != null ? existingByPartId.get(incomingPart.partId) : undefined;
      const existingPartByIndex = existingParts[index];
      const existingPart = existingPartById ?? existingPartByIndex;
      const storedMetadata =
        incomingPart?.partId != null ? this.partIntelligenceByPartId.get(incomingPart.partId) : undefined;

      const mergedPart: PartDetail = {
        ...incomingPart,
        risks: incomingPart.risks ?? storedMetadata?.risks ?? existingPart?.risks ?? [],
        riskCardData: incomingPart.riskCardData ?? storedMetadata?.riskCardData ?? existingPart?.riskCardData,
        riskCardName: incomingPart.riskCardName ?? storedMetadata?.riskCardName ?? existingPart?.riskCardName,
        riskCardDataRaw: incomingPart.riskCardDataRaw ?? storedMetadata?.riskCardDataRaw ?? existingPart?.riskCardDataRaw,
        riskFeedbackAction:
          incomingPart.riskFeedbackAction ?? storedMetadata?.riskFeedbackAction ?? existingPart?.riskFeedbackAction,
      };

      if (mergedPart.partId != null) {
        const metadata = this.extractPartIntelligenceMetadata(mergedPart);
        if (this.hasPartIntelligenceMetadata(metadata)) {
          this.partIntelligenceByPartId.set(mergedPart.partId, metadata);
        }
      }

      return mergedPart;
    });
  }

  private upsertPartIntelligenceStore(parts: PartDetail[]): void {
    parts.forEach((part) => {
      if (part?.partId == null) {
        return;
      }

      const metadata = this.extractPartIntelligenceMetadata(part);
      if (this.hasPartIntelligenceMetadata(metadata)) {
        this.partIntelligenceByPartId.set(part.partId, metadata);
      }
    });
  }

  private extractPartIntelligenceMetadata(part: PartDetail): PartIntelligenceMetadata {
    return {
      risks: part.risks,
      riskCardData: part.riskCardData,
      riskCardName: part.riskCardName,
      riskCardDataRaw: part.riskCardDataRaw,
      riskFeedbackAction: part.riskFeedbackAction,
    };
  }

  private hasPartIntelligenceMetadata(metadata: PartIntelligenceMetadata): boolean {
    return Boolean(
      (metadata.risks && metadata.risks.length > 0) ||
        metadata.riskCardData ||
        metadata.riskCardName ||
        metadata.riskCardDataRaw ||
        metadata.riskFeedbackAction
    );
  }

  onPartClick(partIndex: number): void {
    this.currentPartIndex = partIndex;
    let currentPart: PartDetail | undefined;
    
    if (this.quoteData && this.quoteData.partDetails) {
      this.quoteData.partDetails.forEach((part, index) => {
        part.isActivePart = index === partIndex;
        if(index === partIndex){
          part.isActivePart = true;
          currentPart = part;
        }
      });
      
      // Force change detection to ensure UI updates
      this.cdr.detectChanges();
    }
    // if(currentPart){
    //   currentPart.isPartDetailCompleted = false;
    // }
    this.navigateToStep('parts-information');
    // Switch to the part in the parts-information component
    if (this.partsInformation) {
      if(currentPart){  
        const isError = currentPart.isValidated && !currentPart.isValid;
    
        if((!currentPart.isValidated || isError) && !this.quoteData.isQuoteSubmitted){
          this.partsInformation.switchToPart(partIndex, 'file-upload');
          return;
        }
      }
        this.partsInformation.switchToPart(partIndex, 'configuration');
    }
  }

  onAddPartFiles(partIndex: number): void {
    if(this.getIntegrationType() === 'MFGDirect'){
      this.currentPartIndex = partIndex;
      
      if (this.quoteData && this.quoteData.partDetails) {
        this.quoteData.partDetails.forEach((part, index) => {
          part.isActivePart = index === partIndex;
        });
        this.cdr.detectChanges();
      }
      
      if (this.partsInformation) {
        this.partsInformation.switchToPart(partIndex, 'file-upload');
      }
    }
  }

  onPartFieldChange(event: {
    partIndex: number; 
    field: string; 
    value: any; 
    validationResult: { isValid: boolean; error?: string };
  }): void {
    const { partIndex, field, value, validationResult } = event;
    
    if (partIndex >= 0 && partIndex < this.quoteData.partDetails.length) {
      // Create a new array reference to ensure change detection works properly
      this.quoteData.partDetails = [...this.quoteData.partDetails];
      
      // Update the part field
      (this.quoteData.partDetails[partIndex] as any)[field] = value;
      
      // Handle validation
      if (validationResult.isValid) {
        this.clearValidationError(partIndex, field);
      } else {
        this.storeValidationError(partIndex, field, validationResult.error);
      }
      
      // Save data
      this.saveProgress();
      this.cdr.detectChanges();
      // Manually sync the parts information component to ensure real-time updates
      if (this.partsInformation) {
      //  console.log('[QuoteAssistV2] Manually syncing parts information component');
        this.partsInformation.syncPartsFromParent(this.quoteData.partDetails);
      } else {
        console.log('[QuoteAssistV2] Parts information component not available for manual sync');
      }
    } else {
      console.error(`[QuoteAssistV2] Invalid part index: ${partIndex}, available parts: ${this.quoteData.partDetails.length}`);
    }
  }

  onInternalNotesChange(event: {
    partIndex: number;
    internalNotes: string;
  }): void {
    const { partIndex, internalNotes } = event;
    
    if (partIndex >= 0 && partIndex < this.quoteData.partDetails.length) {
      // Create a new array reference to ensure change detection works properly
      this.quoteData.partDetails = [...this.quoteData.partDetails];
      
      // Update the internal notes
      this.quoteData.partDetails[partIndex].internalNotes = internalNotes;
      
      // Save data
      this.saveProgress();
      this.cdr.detectChanges();
      
      console.log(`[QuoteAssistV2] Internal notes updated for part ${partIndex + 1}:`, internalNotes);
      
      // Manually sync the parts information component to ensure real-time updates
      if (this.partsInformation) {
        console.log('[QuoteAssistV2] Manually syncing parts information component after internal notes update');
        this.partsInformation.syncPartsFromParent(this.quoteData.partDetails);
      }
    }
  }

  private storeValidationError(partIndex: number, field: string, error?: string): void {
    // Store validation error for display
    const errorKey = `part${partIndex}_${field}`;
    if (!this.validationErrors) {
      this.validationErrors = {};
    }
    if (error) {
      this.validationErrors[errorKey] = error;
      // Trigger change detection to update progress-tracker
      this.cdr.detectChanges();
    }
  }

  private clearValidationError(partIndex: number, field: string): void {
    // Clear validation error
    const errorKey = `part${partIndex}_${field}`;
    if (this.validationErrors && this.validationErrors[errorKey]) {
      delete this.validationErrors[errorKey];
      // Trigger change detection to update progress-tracker
      this.cdr.detectChanges();
    }
  }

  onAddNewPart(): void {
    const currentStep = this._progressState$.value.currentStep;    
    if (currentStep !== 'parts-information') {
      this.navigateToStep('parts-information');
      
      setTimeout(() => {
        this.addNewPartAfterNavigation();
      }, 100);
    } else {
      this.addNewPartAfterNavigation();
    }
  }

  private addNewPartAfterNavigation(): void {
    if (this.partsInformation) {
      this.partsInformation.handleAddNewPart();
    }
  }

  onDeletePart(partIndex: number): void {
    if (partIndex < 0 || partIndex >= this.quoteData.partDetails.length) {
      console.error(`[QuoteAssist-V2] Invalid part index for deletion: ${partIndex}`);
      return;
    }

    if (this.quoteData.partDetails.length <= 1) {
      console.warn('[QuoteAssist-V2] Cannot delete the last remaining part');
      return;
    }

    const partName = this.quoteData.partDetails[partIndex]?.partName || `Part ${partIndex + 1}`;
    console.log(`[QuoteAssist-V2] Deleting part: ${partName} (index: ${partIndex})`);
    
    const currentStep = this._progressState$.value.currentStep;
    
    if (currentStep !== 'parts-information') {
      this.navigateToStep('parts-information');
      
      setTimeout(() => {
        this.performDeletePart(partIndex);
      }, 100);
    } else {
      this.performDeletePart(partIndex);
    }
  }

  private performDeletePart(partIndex: number): void {
    if(this.getIntegrationType() === 'MFGDirect'){
      // Call the method on the parts-information component
      if (this.partsInformation) {
        this.partsInformation.removePart(partIndex);
      }

      // Update current part index if the deleted part was the current one
      if (this.currentPartIndex >= this.quoteData.partDetails.length - 1) {
        this.currentPartIndex = Math.max(0, this.quoteData.partDetails.length - 2);
      }
      
      // Update isActivePart for all parts after deletion
      if (this.quoteData && this.quoteData.partDetails) {
        this.quoteData.partDetails.forEach((part, index) => {
          part.isActivePart = index === this.currentPartIndex;
        });
        
        // Force change detection to ensure UI updates
        this.cdr.detectChanges();
      }
    }
  }

  onAddNewPartRequested(): void {
    // This is called when the parts-information component emits addNewPart
    
  }

  onCurrentPartIndexChange(partIndex: number): void {
    // Update the current part index for the progress tracker
    this.currentPartIndex = partIndex;
    
    // Update isActivePart for all parts when current part index changes
    if (this.quoteData && this.quoteData.partDetails) {
      this.quoteData.partDetails.forEach((part, index) => {
        part.isActivePart = index === partIndex;
      });
      
      // Force change detection to ensure UI updates
      this.cdr.detectChanges();
    }
    
    // Save progress to IndexedDB when part index changes
    this.saveProgress();
  }

  /**
   * Initialize parts information component and start quote assist for MFG/SUW integrations
   */
  private initializeAndStartQuoteAssist(): void {
    // Use setTimeout to ensure the ViewChild component is initialized
    setTimeout(() => {
      if (this.partsInformation) {
        this.partsInformation.startQuoteAssist(true);
      } else {
        // If component is still not ready, try again with a longer delay
        setTimeout(() => {
          if (this.partsInformation) {
            this.partsInformation.startQuoteAssist(true);
          }
        }, 200);
      }
    }, 100);
  }
  
  onFileUpload(event: FileUploadEvent): void {
    if (event.success && event.partIndex !== undefined) {
      // Create a new array reference to ensure change detection works properly
      this.quoteData.partDetails = [...this.quoteData.partDetails];
      
      this.quoteData.partDetails[event.partIndex].files.push(...event.files);
      this.saveProgress();
      this.updateProgressState();
      
      // Trigger change detection for OnPush strategy
      this.cdr.detectChanges();
      
      this.logEvent('File Upload Success', { 
        fileCount: event.files.length, 
        partIndex: event.partIndex 
      });
    } else {
      this.handleError('File Upload', new Error(event.error), 'Failed to upload files');
    }
  }
  
  // ===== STEP NAVIGATION =====
  
  /**
   * Reset edit mode state
   */
  private resetEditMode(): void {
    console.log(`[QuoteAssistV2] Resetting edit mode from ${this.isEditMode} to false`);
    this.isEditMode = false;
  }
  
  navigateToStep(stepId: string): void {
    const currentStep = this._progressState$.value.currentStep;
    const stepIndex = this.getStepIndex(stepId);
    const currentIndex = this.getStepIndex(currentStep);
    
    // Reset edit mode when navigating to any step other than parts-information
    if (stepId !== 'parts-information') {
      this.resetEditMode();
    }
    
    const newProgressState = {
      ...this._progressState$.value,
      currentStep: stepId,
      canProceed: this.canProceedToStep(stepId),
      canGoBack: stepIndex > 0
    };
    
    this._progressState$.next(newProgressState);
    
    // Update the progress state to trigger change detection
    this.updateProgressState();
    
    // Save progress to IndexedDB when navigating between steps
    this.saveProgress();
    // Reset MFG flag when navigating to parts-information step
    if (stepId === 'parts-information') {
      this.skipToConfigurationForMFG = this.getIntegrationType() === 'MFG' ? true : false;
      
      if (this.getIntegrationType() === 'MFG' || this.getIntegrationType() === 'SUW') {
        this.initializeAndStartQuoteAssist();
      }
    }
    
    // Trigger change detection to ensure template updates
    this.cdr.detectChanges();
  }
  
  nextStep(): void {
    const currentStep = this._progressState$.value.currentStep;
    const nextStep = this.getNextStep(currentStep);
    
    if (nextStep && this.validateCurrentStep()) {
      this.navigateToStep(nextStep);
    }
  }
  
  previousStep(): void {
    const currentStep = this._progressState$.value.currentStep;
    const previousStep = this.getPreviousStep(currentStep);
    
    if (previousStep) {
      this.navigateToStep(previousStep);
    }
  }
  
  // ===== VALIDATION =====
  
  private validateCurrentStep(): boolean {
    const currentStep = this._progressState$.value.currentStep;
    const validationState = this._validationState$.value;
    
    switch (currentStep) {
      case 'details':
        return validationState.details.isValid;
      case 'parts-information':
        return validationState['parts-information'].isValid;
      case 'costs':
        return validationState.costs.isValid;
      default:
        return true;
    }
  }

  private getValidationErrors(): { [stepName: string]: { [fieldName: string]: string } } {
    const validationState = this._validationState$.value;
    const errors: { [stepName: string]: { [fieldName: string]: string } } = {};

    // Check each step for validation errors
    if (!validationState.details.isValid && Object.keys(validationState.details.errors).length > 0) {
      errors['details'] = validationState.details.errors;
    }

    if (!validationState['parts-information'].isValid && Object.keys(validationState['parts-information'].errors).length > 0) {
      errors['parts-information'] = validationState['parts-information'].errors;
    }

    if (!validationState.costs.isValid && Object.keys(validationState.costs.errors).length > 0) {
      errors['costs'] = validationState.costs.errors;
    }

    return errors;
  }

  private formatValidationErrorMessage(validationErrors: { [stepName: string]: { [fieldName: string]: string } }): string {
    if (Object.keys(validationErrors).length === 0) {
      return 'Please complete all required fields';
    }

    const stepNames: { [key: string]: string } = {
      'details': 'Quote Details',
      'parts-information': 'Parts Information',
      'costs': 'Cost Breakdown'
    };

    const errorMessages: string[] = [];

    for (const [stepName, fieldErrors] of Object.entries(validationErrors)) {
      const stepDisplayName = stepNames[stepName] || stepName;
      const fieldErrorList = Object.values(fieldErrors).join(', ');
      errorMessages.push(`${stepDisplayName}: ${fieldErrorList}`);
    }

    return `Please fix the following validation errors:\n${errorMessages.join('\n')}`;
  }
  
  private canProceedToStep(stepId: string): boolean {
    const stepIndex = this.getStepIndex(stepId);
    const validationState = this._validationState$.value;
    const currentStep = this._progressState$.value.currentStep;
    
    // Can always go back
    if (stepIndex < this.getStepIndex(currentStep)) {
      return true;
    }
    
    // Check if previous steps are valid (exclude the target step itself)
    for (let i = 0; i < stepIndex; i++) {
      const step = this.getStepById(i);
      const isStepValid = this.isStepValid(step);
      if (!isStepValid) {
        return false;
      }
    }
    
    return true;
  }
  
  private isStepValid(stepId: string): boolean {
    const validationState = this._validationState$.value;
    
    let isValid = false;
    
    switch (stepId) {
      case 'details':
        isValid = validationState.details.isValid;
        break;
      case 'parts-information':
        isValid = validationState['parts-information'].isValid;
        break;
      case 'costs':
        isValid = validationState.costs.isValid;
        break;
      default:
        isValid = true;
    }
    
    return isValid;
  }
  
  // ===== PROGRESS MANAGEMENT =====
  
  private updateProgressState(): void {
    const currentStep = this._progressState$.value.currentStep;
    const steps = this.getSteps().map(step => ({
      ...step,
      status: this.getStepStatus(step.stepId),
      isAccessible: this.canProceedToStep(step.stepId),
      validationErrors: this.getStepValidationErrors(step.stepId)
    }));
    
    const overallProgress = this.calculateOverallProgress();
    const canProceed = this.canProceedToStep(this.getNextStep(currentStep) || '');
    const canGoBack = this.getStepIndex(currentStep) > 0;
    
    this._progressState$.next({
      currentStep,
      steps,
      overallProgress,
      canProceed,
      canGoBack
    });
  }
  
  private updateValidationState(step: keyof StepValidationState, event: any): void {
    const currentState = this._validationState$.value;
    const newState = {
      ...currentState,
      [step]: {
        isValid: event.isValid,
        errors: event.errors || {},
        warnings: event.warnings || {},
        touched: event.touched || {}
      }
    };
    
    this._validationState$.next(newState);
  }
  
  // ===== DATA PERSISTENCE =====
  
  /**
   * Save data to IndexedDB (following V1 pattern)
   * @param copySummary Optional parameter for copying summary data
   * @returns Observable with rfqId (like V1)
   */
  saveData(copySummary: boolean = false): Observable<string> {
    // Update lastUpdated timestamp
    this.quoteData.lastUpdated = new Date().toISOString();
    
    // Ensure rfqId is set (required for V1 pattern)
    if (!this.quoteData.quoteDetails.rfqId) {
      return new Observable(observer => {
        observer.error('rfqId is required for IndexedDB storage');
      });
    }
    
    return this.quoteAssistV2Service.saveQuoteAssistV2Progress(this.quoteData);
  }

  /**
   * Save complete data to both IndexedDB and backend
   * @param copySummary Whether to copy summary data
   * @returns Observable with rfqId
   */
  saveCompleteData(copySummary: boolean = false): Observable<string> {
    return new Observable<string>(observer => {
      // First save to IndexedDB
      this.quoteAssistV2Service.saveQuoteAssistV2Progress(this.quoteData).subscribe({
        next: (rfqId) => {
          // Then save to backend using the new draft method
          this.quoteAssistV2Service.draftDirectRFQQuoteData(
            this.quoteData,
            this.queryParams.companyId || 0,
            this.queryParams.contactId || 0,
            this.queryParams.integration || 'MFG'
          ).subscribe({
            next: (backendResponse: number) => {
              observer.next(rfqId);
              observer.complete();
            },
            error: (backendError: any) => {
              // Still return success since IndexedDB saved
              observer.next(rfqId);
              observer.complete();
            }
          });
        },
        error: (indexedDBError: any) => {
          // Try to save to backend anyway
          this.quoteAssistV2Service.draftDirectRFQQuoteData(
            this.quoteData,
            this.queryParams.companyId || 0,
            this.queryParams.contactId || 0,
            this.queryParams.integration || 'MFG'
          ).subscribe({
            next: (backendResponse: number) => {
              observer.next('backend_only');
              observer.complete();
            },
            error: (backendError: any) => {
              observer.error(backendError);
            }
          });
        }
      });
    });
  }


  
  private saveProgress(): void {
    if (!this.quoteData.quoteDetails.rfqId) {
      return;
    }
    this.updateProgressStateInQuoteData();

    const saveEvent: SaveProgressEvent = {
      rfqId: this.quoteData.rfqId,
      step: this._progressState$.value.currentStep,
      data: this.quoteData,
      timestamp: new Date().toISOString(),
      success: false
    };
    
    this.quoteAssistV2Service.saveQuoteAssistV2Progress(this.quoteData)
      .subscribe({
        next: (rfqId) => {
          saveEvent.success = true;
          this.logEvent('Progress Saved', { rfqId });
        },
        error: (error) => {
          saveEvent.error = error.message;
          this.handleError('Progress Save', error, 'Failed to save progress');
        }
      });
  }

  private updateProgressStateInQuoteData(): void {
    if (!this.quoteData) return;
    
    const progressState = this._progressState$.value;
    this.quoteData.currentStep = progressState.currentStep;
    this.quoteData.currentPartIndex = this.currentPartIndex;
    this.quoteData.stepProgress = progressState.steps;
    this.quoteData.overallProgress = progressState.overallProgress;
    this.quoteData.lastActiveTimestamp = new Date().toISOString();
  }

  private restoreProgressFromQuoteData(): void {
    if (!this.quoteData) return;
    
    // Restore current step
    if (this.quoteData.currentStep) {
      const progressState = this._progressState$.value;
      progressState.currentStep = this.quoteData.currentStep;
      this._progressState$.next(progressState);
    }
    
    // Restore current part index
    if (this.quoteData.currentPartIndex !== undefined) {
      this.currentPartIndex = this.quoteData.currentPartIndex;
    }
    
    // Restore step progress
    if (this.quoteData.stepProgress) {
      const progressState = this._progressState$.value;
      progressState.steps = this.quoteData.stepProgress;
      this._progressState$.next(progressState);
    }
    
    // Restore overall progress
    if (this.quoteData.overallProgress !== undefined) {
      const progressState = this._progressState$.value;
      progressState.overallProgress = this.quoteData.overallProgress;
      this._progressState$.next(progressState);
    }
  }
  
  // ===== QUOTE SUBMISSION =====

  /**
   * Transform QuoteAssistV2Data to marketplace format for MFG integration
   */
  private transformToMarketplaceFormat(companyId: number, quoteStatus: string): any {
    const transformedData = {
      CompanyId: companyId,
      SupplierId: this.queryParams.contactId,
      RfqId: parseInt(this.quoteData.rfqId),
      QuoteStatus: quoteStatus,
      RfqPartsMetaData: JSON.stringify(this.transformRfqPartsMetaData()),
      Source: this.quoteData.sourceType
    };
    
    return transformedData;
  }

  /**
   * Transform QuoteAssistV2Data to V2 marketplace format for MFG integration
   */
  private transformToMarketplaceFormatV2(companyId: number, quoteStatus: string): SaveQASupplierRfqQuoteDataV2Request {
    const transformedData: SaveQASupplierRfqQuoteDataV2Request = {
      companyId: companyId,
      supplierId: this.queryParams.contactId || 0,
      rfqId: parseInt(this.quoteData.rfqId),
      buyerName: this.quoteData.quoteDetails.companyName || '',
      buyerCompanyEmail: this.quoteData.quoteDetails.companyEmail || '',
      quoteStatus: quoteStatus,
      rfqPartsMetaData: JSON.stringify(this.transformRfqPartsMetaDataV2()),
      createdOn: new Date().toISOString(),
      source: this.quoteData.sourceType || 'MFG'
    };
    
    return transformedData;
  }

  /**
   * Transform the internal data structure to match marketplace RfqPartsMetaData format
   */
  private transformRfqPartsMetaData(): any {
    return {
      rfqId: parseInt(this.quoteData.rfqId),
      rfqName: this.quoteData.quoteDetails.rfqName,
      companyId: this.queryParams.companyId,
      supplierId: this.queryParams.contactId,
      rfqPartList: this.transformPartDetails(),
      buyerAddress: this.formatBuyerAddress(),
      buyerEmail: this.quoteData.quoteDetails.companyEmail,
      supplierMetaData: this.transformSupplierMetadata(),
      data: {},
      quoteExpiryDate: this.quoteData.quoteDetails.quoteExpiryDate,
      paymentTerm: this.quoteData.quoteDetails.paymentTerm,
      quoteNumber: this.quoteData.quoteDetails.quoteNumber,
      rfqMetaData: this.transformRfqMetadata()
    };
  }

  /**
   * Transform the internal data structure to match V2 marketplace RfqPartsMetaData format
   */
  private transformRfqPartsMetaDataV2(): any {
    return {
      partDetails: this.quoteData.partDetails.map((part, index) => ({
        partName: part.partName || '',
        partNumber: part.partNumber || `PART-${index + 1}`,
        rfqPartId: part.partId || index + 1,
        notesForBuyer: part.notesForBuyer || '',
        isAiDataLoaded: part.isAiDataLoaded || false,
        isPartDetailCompleted: part.isPartDetailCompleted || false,
        files: part.files || [],
        quantityPricing: part.quantityPricing
          .filter((qty: any) => qty.quantity && qty.quantity > 0)
          .map((qty: any, qtyIndex: number) => ({
            quantity: qty.quantity || 0,
            order: qtyIndex + 1,
            unitCost: qty.unitCost || 0,
            markup: qty.markup || 0,
            unitPrice: qty.unitPrice || 0,
            unitPriceTotal: qty.total || 0,
            leadtimeUnit: qty.leadtimeUnit || 'Days',
            leadtimeValue: qty.leadtimeValue || 0
          })),
        partConfiguration: {
          materials: part.configuration?.materials || [],
          tools: part.configuration?.tools || [],
          processes: part.configuration?.processes || [],
          additionalCosts: part.configuration?.additionalCosts || []
        }
      })),
      quoteDetails: {
        paymentTerm: this.quoteData.quoteDetails.paymentTerm || '',
        quoteExpiryDate: this.quoteData.quoteDetails.quoteExpiryDate || '',
        quoteNumber: this.quoteData.quoteDetails.quoteNumber || ''
      },
      stepProgress: this.quoteData.stepProgress || []
    };
  }

  /**
   * Transform part details to marketplace format
   */
  private transformPartDetails(): any[] {
    return this.quoteData.partDetails.map((part, index) => ({
      rfqPartId: part.partId || index + 1,
      partName: part.partName,
      notesForBuyer: part.notesForBuyer || "",
      process: "",
      technique: "",
      material: this.extractMaterialInfo(part),
      postProduction: this.extractPostProductionInfo(part),
      tolerance: null,
      width: null,
      height: null,
      depth: null,
      length: null,
      diameter: null,
      dimensionUnit: null,
      geometry: null,
      qty1: part.quantityPricing[0]?.quantity || 0,
      qty2: part.quantityPricing[1]?.quantity || null,
      qty3: part.quantityPricing[2]?.quantity || null,
      rfqNda: "",
      rfqLocation: "",
      pdfFiles: this.transformPdfFiles(part.files),
      isAssistEnabled: true,
      isAssistSkipped: false,
      isPartCompleted: part.isPartDetailCompleted || false,
      quoteAssistData: this.transformQuoteAssistData(part),
      partQuantity: this.transformPartQuantity(part),
      isVisible: true
    }));
  }

  /**
   * Transform cost breakdown to quote assist data format
   */
  private transformQuoteAssistData(part: any): any {
    return {
      assistedQty: part.quantityPricing[0]?.quantity || 0,
      quoteModelLoaded: true,
      quoteAssistModel: {
        complexityModel: this.transformComplexityModel(),
        materials: this.transformMaterialsModel(part),
        tooling: this.transformToolingModel(part),
        manufacturingProcess: this.transformManufacturingProcessModel(part),
        machining: this.transformMachiningModel(part),
        labor: this.transformLaborModel(part),
        additionalRequirements: this.transformAdditionalRequirementsModel(part),
        overhead: this.transformOverheadModel(part),
        otherCosts: this.transformOtherCostsModel(part),
        shipping: this.transformShippingModel(part),
        profit: this.transformProfitModel(part)
      },
      partQuantity: this.transformPartQuantity(part)
    };
  }

  /**
   * Transform part quantity information
   */
  private transformPartQuantity(part: any): any[] {
    return part.quantityPricing
      .filter((qty: any) => qty.quantity && qty.quantity > 0) // Only include quantities > 0
      .map((qty: any, index: number) => ({
        order: index + 1,
        qty: qty.quantity || 0,
        qtyCompleted: true,
        isAssisted: false,
        summaryData: {
          isCopied: false,
          isCompleted: true,
          isDataLoaded: false,
          tooling: { totalCost: 0, cost: 0, toolingList: [], profitMargin: 0, profitValue: 0 },
          pricePerUnit: {
            materials: { totalCost: 0, unitCost: 0 },
            machining: { totalCost: 0, machineList: [{ cyclePerUnit: 0, costPerMin: 0 }] },
            labor: { totalCost: 0 },
            additionalManufacturingRequirements: { totalCost: 0 },
            overhead: { totalCost: 0 },
            totalPrice: qty.unitPrice?.toString() || "0.00",
            profitMargin: 0,
            profitValue: 0
          },
          miscellaneous: { cost: 0, totalCost: 0, itemList: [], profitMargin: 0, profitValue: 0 },
          shippingHandling: { cost: 0, totalCost: 0, profitMargin: 0, profitValue: 0 },
          leadTime: this.formatLeadTime(qty.leadtimeValue, qty.leadtimeUnit)
        },
        isVisible: false,
        quantityUnit: "Unit",
        doCalculation: false,
        totalCost: qty.total || 0
      }));
  }

  /**
   * Transform PDF files to marketplace format
   */
  private transformPdfFiles(files: any[]): any[] {
    return files.map(file => ({
      fileId: Math.floor(Math.random() * 1000000), // Generate random ID
      fileUrl: file.fileURL,
      extractedContent: file.extractedContent || ""
    }));
  }

  /**
   * Transform supplier metadata
   */
  private transformSupplierMetadata(): any {
    // Use stored supplier metadata if available, otherwise return defaults
    if (this.supplierMetaData) {
      return { ...this.supplierMetaData };
    }
    return {
      companyName: "", // Default value
      companyUrl: "",
      employeeSize: "",
      myCapabilities: ["Fabrication", "Machining", "3D Printing"],
      myEquipment: [""],
      industryServing: [""],
      myAddress: "United States",
      zipCode: "",
      processRates: [],
      additionalInfo: []
    };
  }

  /**
   * Update process rates in supplier metadata from all parts' processes
   * Stores raw cost values for backend calculation
   */
  private updateProcessRatesFromParts(): void {
    if (!this.supplierMetaData) {
      this.supplierMetaData = {
        companyName: '',
        employeeSize: '',
        myCapabilities: [],
        myEquipment: [],
        industryServing: [],
        myAddress: '',
        zipCode: '',
        processRates: [],
        additionalInfo: []
      };
    }

    if (!this.supplierMetaData.processRates || !Array.isArray(this.supplierMetaData.processRates)) {
      this.supplierMetaData.processRates = [];
    }

    // Collect all unique processes from all parts
    const processMap = new Map<string, any>();

    this.quoteData.partDetails.forEach(part => {
      if (part.configuration && part.configuration.processes) {
        part.configuration.processes.forEach((process: any) => {
          if (process.name && process.name.trim() !== '') {
            const processName = process.name.trim();
            
            // If process already exists, update it; otherwise create new entry
            if (processMap.has(processName)) {
              const existing = processMap.get(processName);
              // Update with latest values (prefer non-null values)
              if (process.machineCost !== null && process.machineCost !== undefined) {
                existing.machineCost = process.machineCost;
                existing.machineCostType = process.machineCostType || 'Per Unit';
              }
              if (process.setupCost !== null && process.setupCost !== undefined) {
                existing.setupCost = process.setupCost;
                existing.setupCostType = process.setupCostType || 'Per Unit';
              }
            } else {
              processMap.set(processName, {
                processName: processName,
                machineName: processName,
                machineCost: process.machineCost !== null && process.machineCost !== undefined ? process.machineCost : null,
                setupCost: process.setupCost !== null && process.setupCost !== undefined ? process.setupCost : null,
                machineCostType: process.machineCostType || 'Per Unit',
                setupCostType: process.setupCostType || 'Per Unit',
                overhead: ''
              });
            }
          }
        });
      }
    });

    // Update processRates array with collected processes
    this.supplierMetaData.processRates = Array.from(processMap.values());
  }

  /**
   * Update supplier metadata on the backend
   * Transforms camelCase to snake_case and saves process rates
   */
  updateSupplierMetadata(): void {
    // First, update process rates from all parts
    this.updateProcessRatesFromParts();

    // Initialize supplier metadata if not already set
    if (!this.supplierMetaData) {
      this.supplierMetaData = {
        companyName: '',
        employeeSize: '',
        myCapabilities: [],
        myEquipment: [],
        industryServing: [],
        myAddress: '',
        zipCode: '',
        processRates: [],
        additionalInfo: []
      };
    }

    // Ensure processRates is an array
    if (!this.supplierMetaData.processRates || !Array.isArray(this.supplierMetaData.processRates)) {
      this.supplierMetaData.processRates = [];
    }

    // Transform camelCase to snake_case for backend
    const transformedMetaData = {
      company_name: this.supplierMetaData.companyName || '',
      employee_size: this.supplierMetaData.employeeSize || '',
      my_capabilities: this.supplierMetaData.myCapabilities || [],
      my_equipment: this.supplierMetaData.myEquipment || [],
      industry_serving: this.supplierMetaData.industryServing || [],
      my_address: this.supplierMetaData.myAddress || '',
      zip_code: this.supplierMetaData.zipCode || '',
      process_rates: this.supplierMetaData.processRates || [],
      additional_info: this.supplierMetaData.additionalInfo || []
    };

    const payload = {
      CompanyId: parseInt((this.quoteData.quoteDetails.companyId || this.queryParams.companyId || 0).toString()),
      MetaData: JSON.stringify(transformedMetaData)
    };

    this.quoteAssistV2Service.saveCompanyMetaData(payload).subscribe({
      next: (response: number) => {
        // Metadata updated successfully
      },
      error: (error) => {
        console.error('Error updating company metadata: ', error);
      }
    });
  }

  /**
   * Transform RFQ metadata
   */
  private transformRfqMetadata(): any[] {
    return this.quoteData.partDetails.map((part, index) => ({
      rfqPartId: part.partId,
      partName: part.partName,
      notesForBuyer: part.notesForBuyer || "",
      process: "",
      technique: "",
      material: this.extractMaterialInfo(part),
      postProduction: this.extractPostProductionInfo(part),
      tolerance: null,
      width: null,
      height: null,
      depth: null,
      length: null,
      diameter: null,
      dimensionUnit: null,
      geometry: null,
      qty1: part.quantityPricing[0]?.quantity || 0,
      qty2: part.quantityPricing[1]?.quantity || null,
      qty3: part.quantityPricing[2]?.quantity || null,
      rfqNda: "",
      rfqLocation: "",
      pdfFiles: this.transformPdfFiles(part.files),
      isAssistEnabled: true
    }));
  }

  /**
   * Extract material information from part
   */
  private extractMaterialInfo(part: any): string {
    if (part.configuration?.materials?.length > 0) {
      return part.configuration.materials[0].name;
    }
    return "Standard Material";
  }

  /**
   * Extract post production information from part
   */
  private extractPostProductionInfo(part: any): string {
    if (part.configuration?.additionalCosts?.length > 0) {
      return part.configuration.additionalCosts[0].name;
    }
    return "Standard Finish";
  }

  /**
   * Format buyer address
   */
  private formatBuyerAddress(): string {
    const details = this.quoteData.quoteDetails;
    return `${details.address}, ${details.city}, ${details.state}, ${details.zipCode}, United States`;
  }

  // Helper methods for transforming cost breakdown sections
  private transformComplexityModel(): any {
    return {
      complexity: "2",
      reason: "Standard manufacturing complexity",
      totalCost: 0,
      isVisible: false,
      isDataLoaded: true,
      isLoading: false,
      retryCount: 0,
      addNewVisible: false,
      isSectionCompleted: false,
      chatHistory: [{ Text: "Complexity: 2 - Standard manufacturing complexity", IsUser: false }],
      isEditable: false,
      canCalculate: false,
      sectionName: "Complexity"
    };
  }

  private transformMaterialsModel(part: any): any {
    const materials = part.configuration?.materials || [];
    const totalCost = materials.reduce((sum: number, mat: any) => sum + (mat.cost || 0), 0);
    
    return {
      materialsList: materials.map((mat: any) => ({
        name: mat.name,
        weightPerPart: "0.1",
        totalWeight: "10",
        rawMaterialPricePerPound: 10,
        totalMaterialCost: (mat.cost || 0).toString(),
        description: "Standard material specification"
      })),
      totalCost: totalCost.toString(),
      isVisible: true,
      isDataLoaded: true,
      isLoading: false,
      retryCount: 0,
      addNewVisible: false,
      isSectionCompleted: false,
      chatHistory: [{ Text: `${materials[0]?.name || 'Standard Material'} - Standard material specification`, IsUser: false }],
      isEditable: false,
      canCalculate: false,
      sectionName: "Materials"
    };
  }

  private transformToolingModel(part: any): any {
    const tools = part.configuration?.tools || [];
    const totalCost = tools.reduce((sum: number, tool: any) => sum + (tool.cost || 0), 0);
    
    return {
      productName: part.partName,
      quantityQuoted: part.quantityPricing[0]?.quantity?.toString() || "0",
      toolingList: tools.map((tool: any) => ({
        toolName: tool.name,
        estimatedCost: tool.cost?.toString() || "0",
        category: "Primary",
        purpose: tool.reference || "Standard tooling"
      })),
      totalCost: totalCost,
      isVisible: false,
      isDataLoaded: true,
      isLoading: false,
      retryCount: 0,
      addNewVisible: false,
      isSectionCompleted: false,
      chatHistory: tools.map((tool: any) => ({ Text: `${tool.name} - ${tool.reference || 'Standard tooling'}`, IsUser: false })),
      isEditable: false,
      canCalculate: false,
      sectionName: "Tooling"
    };
  }

  private transformManufacturingProcessModel(part: any): any {
    const processes = part.configuration?.processes || [];
    const totalCost = processes.reduce((sum: number, proc: any) => 
      sum + (proc.machineCost || 0) + (proc.setupCost || 0), 0);
    
    return {
      totalCost: totalCost,
      isVisible: false,
      isDataLoaded: true,
      isLoading: false,
      retryCount: 0,
      addNewVisible: false,
      isSectionCompleted: false,
      chatHistory: [{ Text: "Manufacturing - Standard manufacturing processes", IsUser: false }],
      manufacturingList: processes.map((proc: any) => ({
        processName: proc.name,
        description: proc.reference || "Standard manufacturing process",
        cycleTimePerUnit: "10",
        totalMachineTimeInMinutes: "1000",
        totalLaborTimeInMinutes: "60",
        machineCostPerMinute: "1",
        laborCostPerMinute: "0.5",
        totalMachiningCost: proc.machineCost || 0,
        totalLaborCost: proc.setupCost || 0
      })),
      canCalculate: false,
      isEditable: false,
      sectionName: "ManufacturingProcess"
    };
  }

  private transformMachiningModel(part: any): any {
    return {
      totalCost: 0,
      isVisible: false,
      isDataLoaded: true,
      isLoading: false,
      retryCount: 0,
      addNewVisible: false,
      isSectionCompleted: false,
      chatHistory: [{ Text: "Machining - Standard machining processes", IsUser: false }],
      machiningList: [],
      canCalculate: false,
      isEditable: false,
      sectionName: "Machining"
    };
  }

  private transformLaborModel(part: any): any {
    return {
      totalCost: 0,
      isVisible: false,
      isDataLoaded: true,
      isLoading: false,
      retryCount: 0,
      addNewVisible: false,
      isSectionCompleted: false,
      chatHistory: [{ Text: "Labor - Standard labor costs", IsUser: false }],
      laborList: [],
      canCalculate: false,
      isEditable: false,
      sectionName: "Labor"
    };
  }

  private transformAdditionalRequirementsModel(part: any): any {
    const additionalCosts = part.configuration?.additionalCosts || [];
    const totalCost = additionalCosts.reduce((sum: number, cost: any) => sum + (cost.cost || 0), 0);
    
    return {
      totalCost: totalCost,
      isVisible: false,
      isDataLoaded: true,
      isLoading: false,
      retryCount: 0,
      addNewVisible: false,
      isSectionCompleted: false,
      chatHistory: additionalCosts.map((cost: any) => ({ Text: `${cost.name} - ${cost.reference || 'Standard requirement'}`, IsUser: false })),
      additionalRequirementList: additionalCosts.map((cost: any) => ({
        processName: cost.name,
        estimatedCost: cost.cost?.toString() || "0",
        category: "Primary",
        purpose: cost.reference || "Standard requirement"
      })),
      isEditable: false,
      canCalculate: false,
      sectionName: "AMR"
    };
  }

  private transformOverheadModel(part: any): any {
    return {
      totalCost: 0,
      isVisible: false,
      isDataLoaded: true,
      isLoading: false,
      retryCount: 0,
      addNewVisible: false,
      isSectionCompleted: false,
      chatHistory: [{ Text: "Facility Overhead - Standard overhead costs", IsUser: false }],
      overheadList: [{
        overheadTitle: "Facility Overhead",
        estimatedCost: "20",
        category: "Primary",
        purpose: "Standard overhead costs"
      }],
      canCalculate: false,
      isEditable: false,
      sectionName: "Overhead"
    };
  }

  private transformOtherCostsModel(part: any): any {
    return {
      totalCost: 0,
      isVisible: false,
      isDataLoaded: true,
      isLoading: false,
      retryCount: 0,
      addNewVisible: false,
      isSectionCompleted: false,
      chatHistory: [{ Text: "Miscellaneous - General expenses", IsUser: false }],
      otherCostList: [{
        processName: "Miscellaneous",
        estimatedCost: "50",
        description: "General expenses"
      }],
      canCalculate: false,
      isEditable: false,
      sectionName: "OtherCosts"
    };
  }

  private transformShippingModel(part: any): any {
    return {
      totalCost: 0,
      isVisible: false,
      isDataLoaded: true,
      isLoading: false,
      retryCount: 0,
      addNewVisible: false,
      isSectionCompleted: false,
      chatHistory: [{ Text: "Shipping Charges: 0, Handling Charges: 0", IsUser: false }],
      shippingCharges: 0,
      handlingCharges: 0,
      canCalculate: false,
      sectionName: "Shipping"
    };
  }

  private transformProfitModel(part: any): any {
    return {
      totalCost: 0,
      isVisible: false,
      isDataLoaded: true,
      isLoading: false,
      retryCount: 0,
      addNewVisible: false,
      isSectionCompleted: false,
      chatHistory: [{ Text: "Profit Margin: 30 - Standard profit margin", IsUser: false }],
      industryStandardProfitMargin: "30",
      description: "Standard profit margin for manufacturing services",
      totalPrice: 0,
      pricePerUnit: 0,
      canCalculate: false,
      sectionName: "Profit",
      manufacturingCategory: "Manufacturing"
    };
  }

  submitQuote(action: 'submit' | 'draft' = 'submit', closePopup: boolean = true): void {
    if(this.quoteData.isQuoteSubmitted){
      action = 'submit';
    }
    this.isLoading = true;
    const loadingMessage = action === 'submit' ? 'Submitting your quote...' : 'Saving draft...';
    this.loadingService.showActionLoading('quote submission', loadingMessage, 'full-page');
    // Get the latest quote details from the form component
    // let latestQuoteDetails = this.quoteData.quoteDetails;
    // if (this.quoteDetailsForm) {
    //   latestQuoteDetails = this.quoteDetailsForm.getFormData();
            
    //   // Update the quoteData with the latest form data
    //   this.quoteData.quoteDetails = latestQuoteDetails;
    // }
    
    const submissionEvent: QuoteSubmissionEvent = {
      rfqId: this.quoteData.rfqId,
      quoteData: this.quoteData,
      submissionStatus: 'submitting',
      timestamp: new Date().toISOString()
    };

    const companyId = this.queryParams.companyId || 0;
    
    // Determine the appropriate QuoteStatus based on action
    const quoteStatus = action === 'submit' ? QuoteStatus.Quoted : QuoteStatus.Draft;
    this.quoteData.isQuoteSubmitted = quoteStatus === QuoteStatus.Quoted ? true : false;
    this.saveProgress();
    // this.saveData();
    
    // Call different service functions based on integration type
    let sourceType = this.getIntegrationType();

    const serviceCall = sourceType === 'MFG' 
      ? this.quoteAssistV2Service.draftMarketplaceRFQQuoteDataV2(this.transformToMarketplaceFormatV2(companyId, quoteStatus))
      : this.quoteAssistV2Service.draftDirectRFQQuoteData(
          this.quoteData, 
          companyId, 
          this.queryParams.contactId || 0,
          sourceType,
          quoteStatus, 
          this.quoteData.quoteDetails.companyName,
          this.quoteData.quoteDetails.companyEmail
        );
    
    serviceCall.subscribe({
      next: (response: number) => {
        submissionEvent.submissionStatus = 'success';
        submissionEvent.quoteId = response.toString();
        
        if (action === 'submit') {
          this.logEvent('Quote Generated Successfully', { quoteId: response });
          
          // Set isQuoteSubmitted to true on successful submission
          this.quoteData.isQuoteSubmitted = true;
          
          // Trigger change detection to update UI
          this.cdr.detectChanges();
          
          if(this.getIntegrationType() === 'MFG'){
            this.toastrService.success('Quote generated successfully');
            this.addTracking(MixPanelEvents.QuoteSubmitted, this.queryParams);
            
            // Close popup if requested
            if (closePopup) {
              if (window.parent) {
                window.parent.postMessage({
                  redirect: true,
                  isSubmitted: true,
                  closeIframePopup: true
                }, '*');
              }
            }
          }else{
              this.addTracking(MixPanelEvents.SendViaEmail, this.queryParams);
            this.quoteDownloadedOrMailSentSuccess = true;
          
            // Close popup if requested
            if (closePopup) {
              if (window.parent) {
                window.parent.postMessage({
                  redirect: true,
                  isSubmitted: true,
                  closeIframePopup: true
                }, '*');
              }
            }
          }         
        } else {
          this.logEvent('Draft Saved Successfully', { draftId: response });
          const mixPanelEvent = this.getMixPanelEventForIntegrationDraft(this.getIntegrationType());
            this.addTracking(mixPanelEvent, this.queryParams);
          // this.toastrService.success('Draft saved successfully');
          if (window.parent && closePopup) {
            window.parent.postMessage({
              redirect: true,
              isSubmitted: true,
              closeIframePopup:true
            }, '*');
          }
        }
        
        this.isLoading = false;
        this.loadingService.hideLoading();
      },
      error: (error: any) => {
        submissionEvent.submissionStatus = 'error';
        submissionEvent.error = error.message;
        const errorContext = action === 'submit' ? 'Quote Generation' : 'Draft Save';
        const errorMessage = action === 'submit' ? 'Failed to generate quote' : 'Failed to save draft';
        
        // Show error notification
        this.toastrService.error(errorMessage);
        
        this.handleError(errorContext, error, errorMessage);
        this.isLoading = false;
        this.loadingService.hideLoading(); // ✅ Fix: Hide loading service state on error too
      }
    });
  }



  /**
   * Submit all parts with their files and configurations using the parts information component
   * This method calls the new submitAllPartsWithFiles() method directly
   */
  submitAllPartsWithFiles(): void {
    if (!this.partsInformation) {
      console.error('Parts Information component not available');
      this.handleError('Parts Submission', new Error('Component not available'), 'Parts component not initialized');
      return;
    }

    // Check if we're on the parts step
    const currentStep = this._progressState$.value.currentStep;
    if (currentStep !== 'parts-information') {
      console.warn('Not on parts step, current step:', currentStep);
      // You can still submit parts even if not on the step, but log it
    }

    // Validate parts before submission
    if (this.quoteData.partDetails.length === 0) {
      this.handleError('Parts Submission', new Error('No parts available'), 'No parts to submit');
      return;
    }

    console.log('[Quote-Assist-V2] Submitting all parts with files...', {
      partsCount: this.quoteData.partDetails.length,
      parts: this.quoteData.partDetails.map(p => ({ name: p.partName, number: p.partNumber }))
    });

    // Call the parts information component method directly
    this.partsInformation.submitAllPartsWithFiles();
  }
  
  // ===== ERROR HANDLING =====
  
  private handleError(context: string, error: any, userMessage: string): void {
    console.error(`Error in ${context}:`, error);
    
    const componentError: ComponentError = {
      component: context,
      error: error.message || error,
      timestamp: new Date().toISOString(),
      severity: 'medium',
      userMessage,
      technicalDetails: error.stack
    };
    
    const currentErrorState = this._errorState$.value;
    const newErrorState: ErrorState = {
      hasErrors: true,
      errors: [...currentErrorState.errors, componentError],
      warnings: currentErrorState.warnings,
      criticalErrors: componentError.severity === 'critical' 
        ? [...currentErrorState.criticalErrors, componentError]
        : currentErrorState.criticalErrors
    };
    
    this._errorState$.next(newErrorState);
    
    // For integration-specific errors, provide more context
    if (context.includes('MFG') || context.includes('Direct') || context.includes('Integration')) {
      const integration = this.queryParams.integration;
      console.warn(`Integration: ${integration}, RFQ ID: ${this.queryParams.rfqId}`);
    }
    
    // Show user-friendly error message
    // Note: In a real application, you might want to use a toast service here
    console.warn(`User Message: ${userMessage}`);
  }
  
  private handleValidationErrors(errors?: { [stepName: string]: string[] }): void {
    if (errors) {
      Object.entries(errors).forEach(([step, stepErrors]) => {
        stepErrors.forEach(error => {
          this.handleError('Validation', new Error(error), `Validation error in ${step}: ${error}`);
        });
      });
    }
  }
  
  // ===== UTILITY METHODS =====
  
  private initializeQuoteDataWithDefaultValue(): QuoteAssistV2Data {
    return {
      rfqId: this.queryParams?.rfqId || '', // Top-level rfqId for IndexedDB keyPath
      sourceType: this.queryParams?.integration || 'MFG',
      lastUpdated: new Date().toISOString(),
      quoteDetails: {
        rfqId: this.queryParams?.rfqId || '', 
        rfqName: '',
        companyName: '',
        companyEmail: '',
        companyPhone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        quoteNumber: '',
        quoteExpiryDate: '',
        quoteDate: '',
        paymentTerm: '',
        companyId: this.queryParams?.companyId,
        contactId: this.queryParams?.contactId,
        isDraftEnabled: false,
        isSubmitEnabled: false,
        isQuoteDetailsCompleted: false
      },
      partDetails: [],
      costBreakdown: {
        materials: { totalCost: 0, items: [] },
        tooling: { totalCost: 0, items: [] },
        manufacturing: { totalCost: 0, items: [] },
        labor: { totalCost: 0, items: [] },
        overhead: { totalCost: 0, items: [] },
        otherCosts: { totalCost: 0, items: [] },
        profit: { profitMargin: 15, totalProfit: 0 }
      },
      isConfirmQuote: false, // Initialize as false
      // Initialize progress tracking fields
      currentStep: 'details',
      currentPartIndex: 0,
      stepProgress: this.getSteps(),
      overallProgress: 0,
      lastActiveTimestamp: new Date().toISOString()
    };
  }
  
  private initializeProgressState(): ProgressState {
    return {
      currentStep: 'details',
      steps: this.getSteps(),
      overallProgress: 0,
      canProceed: false,
      canGoBack: false
    };
  }
  
      private initializeValidationState(): StepValidationState {
      return {
        details: { isValid: false, errors: {}, warnings: {}, touched: {} },
        'parts-information': { isValid: false, errors: {}, warnings: {}, touched: {} },
        costs: { isValid: false, errors: {}, warnings: {}, touched: {} }
      };
    }
  
  private initializeErrorState(): ErrorState {
    return {
      hasErrors: false,
      errors: [],
      warnings: [],
      criticalErrors: []
    };
  }
  
  private initializeStepConfig(): StepConfig {
    return {
      details: { readonly: false, showValidation: true, autoSave: true, showHelp: true, allowEdit: true },
      'parts-information': { readonly: false, showValidation: true, autoSave: true, showHelp: true, allowEdit: true },
      costs: { readonly: false, showValidation: true, autoSave: true, showHelp: true, allowEdit: true }
    };
  }
  
  private configureStepValidation(): void {
    // Configure validation rules based on source type
    switch (this.queryParams.integration) {
      case 'MFG':
        this.stepConfig.details.showValidation = true;
        this.stepConfig.details.readonly = false;
        break;
      case 'SUW':
        this.stepConfig.details.showValidation = false;
        this.stepConfig.details.readonly = true;
        break;
      case 'MFGDirect':
        this.stepConfig.details.showValidation = true;
        this.stepConfig.details.readonly = false;
        break;
    }
  }
  
  private getSteps(): StepProgress[] {
    return [
      { stepId: 'details', title: 'Add Quote Details', status: 'pending' as const, isAccessible: true, validationErrors: [] },
      { stepId: 'parts-information', title: 'Parts', status: 'pending' as const, isAccessible: false, validationErrors: [] },
      { stepId: 'costs', title: 'Confirm Quote', status: 'pending' as const, isAccessible: false, validationErrors: [] }
    ];
  }
  
  private getStepIndex(stepId: string): number {
    const steps = this.getSteps();
    const index = steps.findIndex(step => step.stepId === stepId);
    return index;
  }
  
  private getStepById(index: number): string {
    const steps = this.getSteps();
    const step = steps[index]?.stepId || 'details';
    return step;
  }
  
  private getNextStep(currentStep: string): string | null {
    const currentIndex = this.getStepIndex(currentStep);
    const nextIndex = currentIndex + 1;
    return nextIndex < this.getSteps().length ? this.getStepById(nextIndex) : null;
  }
  
  private getPreviousStep(currentStep: string): string | null {
    const currentIndex = this.getStepIndex(currentStep);
    const previousIndex = currentIndex - 1;
    return previousIndex >= 0 ? this.getStepById(previousIndex) : null;
  }
  
  private getStepStatus(stepId: string): 'pending' | 'active' | 'completed' {
    const currentStep = this._progressState$.value.currentStep;
    const currentIndex = this.getStepIndex(currentStep);
    const stepIndex = this.getStepIndex(stepId);
    
    // Special handling for 'details' step - check if quote details are complete
    if (stepId === 'details') {
      if (this.quoteData?.quoteDetails?.isQuoteDetailsCompleted) {
        return 'completed';
      }
      if (stepId === currentStep) return 'active';
      if (stepIndex < currentIndex) return 'completed';
      // if (!this.isStepValid(stepId)) return 'error';
      return 'pending';
    }
    
    // Special handling for 'parts-information' step - check if all parts are complete
    if (stepId === 'parts-information') {
      if (this.quoteData?.partDetails && this.quoteData.partDetails.length > 0) {
        const allPartsCompleted = this.quoteData.partDetails.every(part => part.isPartDetailCompleted);
        if (allPartsCompleted) {
          return 'completed';
        }
      }
      if (stepId === currentStep) return 'active';
      if (stepIndex < currentIndex) return 'completed';
      return 'pending';
    }
    
    if (stepId === currentStep) return 'active';
    if (stepIndex < currentIndex) return 'completed';
    return 'pending';
  }
  
  private getStepValidationErrors(stepId: string): string[] {
    const validationState = this._validationState$.value;
    
    switch (stepId) {
      case 'details':
        return Object.values(validationState.details.errors);
      case 'parts-information':
        return Object.values(validationState['parts-information'].errors);
      case 'costs':
        return Object.values(validationState.costs.errors);
      default:
        return [];
    }
  }
  
  private calculateOverallProgress(): number {
    const steps = this.getSteps();
    const completedSteps = steps.filter(step => this.getStepStatus(step.stepId) === 'completed').length;
    return Math.round((completedSteps / steps.length) * 100);
  }
  
  private calculateTotalCost(): number {
    const costs = this.quoteData.costBreakdown;
    return costs.materials.totalCost +
           costs.tooling.totalCost +
           costs.manufacturing.totalCost +
           costs.labor.totalCost +
           costs.overhead.totalCost +
           costs.otherCosts.totalCost;
  }
  
  private transformApiData(apiData: any): QuoteAssistV2Data {
    const transformedData = {
      ...this.initializeQuoteDataWithDefaultValue(),
      ...apiData
    };
    
    // Ensure top-level rfqId is set from quoteDetails.rfqId if not already set
    if (!transformedData.rfqId && transformedData.quoteDetails?.rfqId) {
      transformedData.rfqId = transformedData.quoteDetails.rfqId;
    }
    
    return transformedData;
  }
  
  private logEvent(eventName: string, data: any): void {
    // Event logging can be implemented here if needed
  }

  private isIndexedDBDataComplete(indexedDBData: QuoteAssistV2Data): boolean {
    if (!indexedDBData || !indexedDBData.quoteDetails) {
      return false;
    }

    const quoteDetails = indexedDBData.quoteDetails;
    
    // Check if essential fields have meaningful values
    const hasCompanyName = !!(quoteDetails.companyName && quoteDetails.companyName.trim().length > 0);
    const hasCompanyEmail = !!(quoteDetails.companyEmail && quoteDetails.companyEmail.trim().length > 0);
    const hasCompanyPhone = !!(quoteDetails.companyPhone && quoteDetails.companyPhone.trim().length > 0);
    const hasRfqName = !!(quoteDetails.rfqName && quoteDetails.rfqName.trim().length > 0);
        
    if (this.queryParams.integration?.toUpperCase() === 'MFG') {
      const result = hasCompanyName && hasRfqName;
      return result;
    }
    
    const result = hasCompanyName && hasCompanyEmail && hasCompanyPhone && hasRfqName;
    return result;
  }

  /**
   * Check if cost breakdown is empty or has no meaningful data
   * @param costBreakdown Cost breakdown object
   * @returns true if cost breakdown is empty, false otherwise
   */
  private isCostBreakdownEmpty(costBreakdown: any): boolean {
    if (!costBreakdown) {
      return true;
    }

    // Check if any section has meaningful data
    const sections = ['materials', 'tooling', 'manufacturing', 'labor', 'overhead', 'otherCosts'];
    const hasData = sections.some(section => {
      const sectionData = costBreakdown[section];
      return sectionData && (
        sectionData.totalCost > 0 || 
        (sectionData.items && sectionData.items.length > 0)
      );
    });

    return !hasData;
  }
  
  // ===== BACKEND OPERATION HANDLING =====
  
  private getStepBackendOperation(stepId: string): BackendOperation | undefined {
    return this.stepBackendOperations.find(operation => operation.stepId === stepId);
  }
  
  private handleStepBackendOperation(
    stepId: string, 
    data: any, 
    preNavigation: boolean = true
  ): Observable<BackendOperationResult> {
    const operation = this.getStepBackendOperation(stepId);
    
    if (!operation) {
      return of({ success: true, stepId });
    }
    
    const transformedData = operation.dataTransformer ? operation.dataTransformer(data) : data;
    
    return new Observable<BackendOperationResult>(observer => {
      operation.apiCall(transformedData).subscribe({
        next: (response) => {
          const result: BackendOperationResult = {
            success: true,
            data: response,
            stepId
          };
          this.handleBackendResponse(stepId, response);
          
          // Add marketplace draft save for MFG integration when proceeding from details step
          // if (stepId === 'details' && this.queryParams.integration === 'MFG') {
          //   this.submitQuote("draft", false);
          // }
          
          observer.next(result);
          observer.complete();
        },
        error: (error) => {
          const result: BackendOperationResult = {
            success: false,
            error: error.message || 'Backend operation failed',
            stepId
          };
          this.handleBackendError(stepId, error);
          observer.error(result);
        }
      });
    });
  }
  
  private handleBackendResponse(stepId: string, response: any): void {
    switch (stepId) {
      case 'details':
        if (response.data?.rfqId) {
          // Preserve the isQuoteComplete flag when updating quote details
          const currentIsQuoteComplete = this.quoteData.quoteDetails?.isQuoteDetailsCompleted;
          this.quoteData.quoteDetails = {
            ...this.quoteData.quoteDetails,
            rfqId: response.data.rfqId,
            isQuoteDetailsCompleted: currentIsQuoteComplete || true // Preserve the flag or set to true for details step
          };
          this.quoteData.rfqId = response.data.rfqId;
        }else{
          this.quoteData.quoteDetails = {
            ...this.quoteData.quoteDetails,
            rfqId: '',
            isQuoteDetailsCompleted: false
          };
        }
        // Save draft data after quote details are successfully saved
        if(this.getIntegrationType() !== 'MFG'){
          this.quoteData.quoteDetails.isQuoteDetailsCompleted = true;
          this.updateProgressState();
          this.updateProgressStateInQuoteData();
          this.saveDraftData('quote-details-completion').subscribe();
        }
        break;
      // case 'parts-information':
      case 'costs':
        // Update last modified timestamp
        this.quoteData.lastUpdated = new Date().toISOString();
        break;
    }
  }

  /**
   * Centralized method to save draft data to backend
   * @param context Optional context for logging purposes
   * @returns Observable with draft save result
   */
  private saveDraftData(context?: string): Observable<number> {
    return this.quoteAssistV2Service.draftDirectRFQQuoteData(
      this.quoteData,
      this.queryParams.companyId || 0,
      this.queryParams.contactId || 0,
      this.queryParams.integration || 'MFG'
    ).pipe(
      tap(response => {
        this.logEvent('Draft Save Success', { context, response });
        // Show success notification for draft save
        // this.toastrService.success('Draft saved successfully');
      }),
      catchError(error => {
        this.logEvent('Draft Save Error', { context, error });
        // Show error notification for draft save
        // this.toastrService.error('Failed to save draft');
        // Don't throw - draft save is secondary and shouldn't block user progression
        return of(0);
      })
    );
  }

  /**
   * Public method to trigger draft save from child components
   * @param context Optional context for logging purposes
   */
  public triggerDraftSave(context?: string): void {
    this.saveDraftData(context).subscribe();
  }

  /**
   * Save marketplace draft for MFG integration
   * @param response Backend response containing rfqId
   * @returns Observable with marketplace draft save result
   */
  private saveMarketplaceDraft(response: any): Observable<SaveMarketplaceDraftResponse> {
    if (!response?.data?.rfqId) {
      return of({ success: false, error: 'No RFQ ID in response' });
    }

    const draftData: MarketplaceDraftModel = this.buildMarketplaceDraftPayload(parseInt(response.data.rfqId));

    return this.quoteAssistV2Service.saveMarketplaceDraft(draftData);
  }

  /**
   * Build marketplace draft payload from RFQ ID
   * @param rfqId RFQ ID to build payload for
   * @returns MarketplaceDraftModel payload
   */
  private buildMarketplaceDraftPayload(rfqId: number): MarketplaceDraftModel {
    return {
      CompanyId: this.quoteData?.quoteDetails?.companyId || 0,
      SupplierId: this.quoteData?.quoteDetails?.contactId || 0,
      RfqId: rfqId,
      QuoteStatus: 'Draft',
      RfqPartsMetaData: JSON.stringify(this.quoteData),
      CreatedOn: new Date().toISOString(),
      Source: 'MFG',
      BuyerName: this.quoteData?.quoteDetails?.companyName || '',
      BuyerCompanyEmail: this.quoteData?.quoteDetails?.companyEmail || '',
      quoteExpiryDate: this.quoteData?.quoteDetails?.quoteExpiryDate || '',
      paymentTerm: this.quoteData?.quoteDetails?.paymentTerm || '',
      quoteNumber: this.quoteData?.quoteDetails?.quoteNumber || ''
    };
  }

  /**
   * Save marketplace draft from draft save response
   * @param draftId Draft ID from regular draft save
   * @returns Observable with marketplace draft save result
   */
  private saveMarketplaceDraftFromDraft(draftId: number): Observable<SaveMarketplaceDraftResponse> {
    if (!this.quoteData.quoteDetails.rfqId) {
      return of({ success: false, error: 'No RFQ ID available' });
    }

    const draftData: MarketplaceDraftModel = this.buildMarketplaceDraftPayload(parseInt(this.quoteData.quoteDetails.rfqId));

    return this.quoteAssistV2Service.saveMarketplaceDraft(draftData);
  }

  // Parse draft V1 metadata once and store for reuse
  private parseDraftV1MetaData(response: any): void {
    try {
      // console.log(this.parsedQuoteDetailsFromDynamo);
      if (response && response?.data?.rfqPartsMetaData) {
        this.parsedDraftV1MetaData = JSON.parse(response.data.rfqPartsMetaData);
        
        // Transform the parsed data to QuoteAssistV2Data format
        this.transformDraftV1DataToQuoteAssistV2(response);
      }
      else{
        // Handle V1 response - the response data itself contains the part information
        // For V1, response.data is an array of parts or contains rfqPartList
        this.parsedDraftV1MetaData = JSON.parse(this.parsedQuoteDetailsFromDynamo.rfqMetaData);
        
        // Transform the parsed data to QuoteAssistV2Data format
        this.transformDraftV1DataToQuoteAssistV2(this.parsedQuoteDetailsFromDynamo);
      }
    } catch (error) {
      console.error('Error parsing draft V1 metadata:', error);
      this.parsedDraftV1MetaData = null;
    }
  }

  /**
   * Transform the parsed draft V1 data to QuoteAssistV2Data format
   */
  private transformDraftV1DataToQuoteAssistV2(response: any): void {
    try {
      // Check if this is a V2 response with rfqPartsMetaData
      if (response && response?.data?.rfqPartsMetaData) {
        this.handleV2Response(response);
        return;
      }

      // Handle V1 response - response.data contains array of parts
      if (!this.parsedDraftV1MetaData) {
        console.warn('No parsed draft V1 metadata available');
        return;
      }

      // For V1, response.data is the array of parts directly
      const v1PartData = this.parsedDraftV1MetaData;
      
      // Transform to QuoteAssistV2Data structure
      const quoteAssistV2Data: QuoteAssistV2Data = {
        rfqId: this.parsedQuoteDetailsFromDynamo.rfqId?.toString() || '',
        sourceType: 'MFG' as RfqSource,
        lastUpdated: new Date().toISOString(),
        quoteDetails: this.transformQuoteDetails(this.parsedQuoteDetailsFromDynamo, response.data),
        partDetails: this.transformPartDetailsFromV1Response(v1PartData),
        costBreakdown: this.createDefaultCostBreakdown(),

        isConfirmQuote: false,
        isQuoteSubmitted: false,
        currentStep: 'quote-details',
        currentPartIndex: 0,
        stepProgress: [],
        overallProgress: 0,
        lastActiveTimestamp: new Date().toISOString()
      };

      // Set the transformed data to the component
      this.quoteData = quoteAssistV2Data;
      this.hasRealData = true;    
      this.saveProgress();  
    } catch (error) {
      console.error('Error transforming draft V1 data to QuoteAssistV2:', error);
    }
  }

  /**
   * Determine current step and part index from stepProgress array
   */
  private determineCurrentStepFromProgress(stepProgress: any[]): { currentStep: string; currentPartIndex: number } {
    if (!Array.isArray(stepProgress) || stepProgress.length === 0) {
      return { currentStep: 'details', currentPartIndex: 0 };
    }

    // Find the first active or completed step
    const activeStep = stepProgress.find(step => step.status === 'active');
    if (activeStep) {
      const activeStepIndex = stepProgress.findIndex(step => step.status === 'active');
      // Map API step IDs to component step IDs
      const mappedStepId = this.mapApiStepIdToComponentStepId(activeStep.stepId);
      return { currentStep: mappedStepId, currentPartIndex: activeStepIndex };
    }

    // If no active step, find the first pending step
    const pendingStep = stepProgress.find(step => step.status === 'pending');
    if (pendingStep) {
      const pendingStepIndex = stepProgress.findIndex(step => step.status === 'pending');
      // Map API step IDs to component step IDs
      const mappedStepId = this.mapApiStepIdToComponentStepId(pendingStep.stepId);
      return { currentStep: mappedStepId, currentPartIndex: pendingStepIndex };
    }

    // Default fallback
    return { currentStep: 'details', currentPartIndex: 0 };
  }

  /**
   * Map API step IDs to component step IDs
   */
  private mapApiStepIdToComponentStepId(apiStepId: string): string {
    // Map common API step ID variations to component step IDs
    const stepIdMapping: { [key: string]: string } = {
      'quote-details': 'details',
      // 'quote_details': 'details',
      // 'quoteDetails': 'details',
      'parts-information': 'parts-information',
      // 'parts_information': 'parts-information',
      // 'partsInformation': 'parts-information',
      'costs': 'costs',
      // 'cost': 'costs',
      // 'quote-confirmation': 'costs',
      // 'quote_confirmation': 'costs',
      // 'quoteConfirmation': 'costs'
    };

    return stepIdMapping[apiStepId] || apiStepId;
  }

  /**
   * Calculate overall progress based on stepProgress array
   */
  private calculateOverallProgressFromStepProgress(stepProgress: any[]): number {
    if (!Array.isArray(stepProgress) || stepProgress.length === 0) {
      return 0;
    }

    const completedSteps = stepProgress.filter(step => step.status === 'completed').length;
    const totalSteps = stepProgress.length;
    
    return totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  }

  private handleV2Response(response: any): void {
    try {
      let parsedMetaData: any;
      try {
        parsedMetaData = JSON.parse(response.data.rfqPartsMetaData);
      } catch (parseError) {
        console.error('Error parsing rfqPartsMetaData JSON:', parseError);
        return;
      }

      // Determine current step and part index from stepProgress
      const { currentStep, currentPartIndex } = this.determineCurrentStepFromProgress(parsedMetaData?.stepProgress || []);

      // Transform to QuoteAssistV2Data structure
      const quoteAssistV2Data: QuoteAssistV2Data = {
        rfqId: response.data.rfqId?.toString() || '',
        sourceType: 'MFG' as RfqSource,
        lastUpdated: new Date().toISOString(),
        quoteDetails: this.transformQuoteDetailsFromV2(response, parsedMetaData),
        partDetails: this.transformPartDetailsFromV2(parsedMetaData?.partDetails || []),
        costBreakdown: this.createDefaultCostBreakdown(),
        isConfirmQuote: false,
        // isQuoteSubmitted: false,
        currentStep: currentStep,
        currentPartIndex: currentPartIndex,
        stepProgress: parsedMetaData?.stepProgress || [],
        overallProgress: this.calculateOverallProgressFromStepProgress(parsedMetaData?.stepProgress || []),
        lastActiveTimestamp: new Date().toISOString(),
        isQuoteSubmitted: response.quoteStatus === QuoteStatus.Quoted ? true : false
      };

      this.skipToConfigurationForMFG = this.getIntegrationType() === 'MFG';      
      // Set the transformed data to the component
      this.quoteData = quoteAssistV2Data;
      // this.navigateToStep('parts-information');
      this.restoreProgressFromQuoteData();
      this.updateProgressState();
      
      this.cdr.detectChanges();
      this.hasRealData = true;    
      this.saveProgress();
    } catch (error) {
      console.error('Error handling V2 response:', error);
    }
  }



  /**
   * Combine buyerCompanyAddress and buyerCompanyAddress2 for Company Address display.
   * When address2 is present, returns "address1, address2"; otherwise address1 only.
   */
  private formatBuyerCompanyAddress(data: {
    buyerCompanyAddress?: string;
    buyerCompanyAddress2?: string;
  } | null | undefined): string {
    const line1 = (data?.buyerCompanyAddress || '').trim();
    const line2 = (data?.buyerCompanyAddress2 || '').trim();
    if (line1 && line2) {
      return `${line1}, ${line2}`;
    }
    return line1 || line2;
  }

  /**
   * Transform quote details from draft V1 data
   */
  private transformQuoteDetails(draftData: any, responseData: any): QuoteDetails {
    return {
      rfqId: draftData.rfqId?.toString(),
      rfqName: draftData.rfqName,
      companyName: this.parsedQuoteDetailsFromDynamo.buyerCompanyName,
      companyEmail: this.parsedQuoteDetailsFromDynamo.buyerCompanyEmail,
      companyPhone: this.parsedQuoteDetailsFromDynamo.buyerCompanyPhone,
      address: this.formatBuyerCompanyAddress(this.parsedQuoteDetailsFromDynamo),
      city: this.parsedQuoteDetailsFromDynamo.buyerCompanyCity,
      state: this.parsedQuoteDetailsFromDynamo.buyerCompanyState,
      zipCode: this.parsedQuoteDetailsFromDynamo.buyerCompanyZipCode,
      quoteNumber: draftData?.quoteNumber,
      quoteExpiryDate: draftData?.quoteExpiryDate,
      paymentTerm: draftData?.paymentTerm,
      companyId: responseData?.companyId || draftData.companyId,
      contactId: this.queryParams.contactId || 0,
      quoteDate: new Date().toISOString().split('T')[0],
      isDraftEnabled: true,
      isSubmitEnabled: false,
      isQuoteDetailsCompleted: false
    };
  }

  /**
   * Transform quote details from V2 API response
   */
  private transformQuoteDetailsFromV2(response: any, parsedMetaData: any): QuoteDetails {
    return {
      rfqId: this.parsedQuoteDetailsFromDynamo.rfqId?.toString(),
      rfqName: this.parsedQuoteDetailsFromDynamo.rfqName,
      companyName: this.parsedQuoteDetailsFromDynamo.buyerCompanyName,
      companyEmail: this.parsedQuoteDetailsFromDynamo.buyerCompanyEmail,
      companyPhone: this.parsedQuoteDetailsFromDynamo.buyerCompanyPhone,
      address: this.formatBuyerCompanyAddress(this.parsedQuoteDetailsFromDynamo),
      city: this.parsedQuoteDetailsFromDynamo.buyerCompanyCity,
      state: this.parsedQuoteDetailsFromDynamo.buyerCompanyState,
      zipCode: this.parsedQuoteDetailsFromDynamo.buyerCompanyZipCode,
      quoteNumber: parsedMetaData?.quoteDetails?.quoteNumber,
      quoteExpiryDate: parsedMetaData?.quoteDetails?.quoteExpiryDate,
      paymentTerm: parsedMetaData?.quoteDetails?.paymentTerm,
      companyId: response.data.companyId || 0,
      contactId: this.queryParams.contactId || 0,
      quoteDate: new Date().toISOString().split('T')[0],
      isDraftEnabled: true,
      isSubmitEnabled: false,
      isQuoteDetailsCompleted: true
    };
  }

  /**
   * Transform part details from draft V1 data
   * Handles both complete draft V1 data and basic metadata (like parsedDraftV1MetaData)
   */
  private transformPartDetailsFromDraftV1(rfqPartList: any[]): PartDetail[] {
    if (!Array.isArray(rfqPartList) || rfqPartList.length === 0) {
      return [];
    }

    return rfqPartList.map((part, index) => {
      const isBasicMetadata = part.hasOwnProperty('qty1') && part.hasOwnProperty('qty2') && part.hasOwnProperty('qty3');
      if (isBasicMetadata) {
        return this.transformBasicMetadataToPartDetail(part, index);
      } 

      const quoteAssistData = part.quoteAssistData || {};
      const quoteAssistModel = quoteAssistData.quoteAssistModel || {};
      
      return {
        partId: part.rfqPartId || index + 1,
        partName: part.partName || `Part ${index + 1}`,
        partNumber: part.part_number?.toString() || (index + 1).toString(),
        files: this.transformPartFilesFromDraftV1(part.pdfFiles || part.pdf_files || []),
        notesForBuyer: part.notesForBuyer || '',
        internalNotes: '',
        quantityPricing: this.transformQuantityPricingFromDraftV1(part.partQuantity || []),
        configuration: this.transformPartConfiguration(quoteAssistModel),
        isPartDetailCompleted: part.isPartCompleted || false,
        extractedContent: this.extractPartContent(part.pdfFiles || part.pdf_files || []),
        isInternalNotesEnabled: false,
        isAiDataLoaded: quoteAssistData.quoteModelLoaded || false
      };
    });
  }

  /**
   * Transform part details from V2 API response
   * Handles the new structure with partConfiguration
   */
  private transformPartDetailsFromV2(partDetails: any[]): PartDetail[] {
    if (!Array.isArray(partDetails) || partDetails.length === 0) {
      return [];
    }

    return partDetails.map((part, index) => ({
      partId: part.rfqPartId || index + 1,
      partName: part.partName || `Part ${index + 1}`,
      partNumber: part.partNumber || (index + 1).toString(),
      files: this.transformPartFilesFromV2(part.rfqPartId || index + 1),
      notesForBuyer: part.notesForBuyer || part.notes_for_buyer || '',
      internalNotes: '',
      quantityPricing: this.transformQuantityPricingFromV2(part.quantityPricing || []),
      configuration: this.transformPartConfigurationFromV2(part.partConfiguration || {}),
      isPartDetailCompleted: part.isPartDetailCompleted || false,
      extractedContent: '',
      isInternalNotesEnabled: false,
      isAiDataLoaded: part.isAiDataLoaded || false,
      isValidated: true,
      isValid: true
    }));
  }

  /**
   * Transform part configuration from V2 API response
   */
  private transformPartConfigurationFromV2(partConfig: any): PartConfiguration {
    return {
      materials: partConfig.materials || [],
      tools: partConfig.tools || [],
      processes: partConfig.processes || [],
      additionalCosts: partConfig.additionalCosts || []
    };
  }

  /**
   * Transform quantity pricing from V2 API response
   */
  private transformQuantityPricingFromV2(quantityPricing: any[]): QuantityPricing[] {
    if (!Array.isArray(quantityPricing) || quantityPricing.length === 0) {
      return [];
    }

    const result = quantityPricing.map((qty, index) => ({
      quantity: qty.quantity || 0,
      leadtimeValue: qty.leadtimeValue || 0,
      leadtimeUnit: qty.leadtimeUnit || 'Days' as const,
      unitCost: qty.unitCost || 0,
      markup: qty.markup || null,
      unitPrice: qty.unitPrice || 0,
      total: qty.unitPriceTotal || 0,
      order: qty.order || index + 1,
      unitPriceTotal: qty.unitPriceTotal || 0,
      isQuantityEditable: true,
      isValidQuantity: !!(qty.quantity && qty.quantity > 0),
      showLeadTimeInputFields: false
    }));

    // Ensure we always have exactly 3 items
    while (result.length < 3) {
      result.push({
        quantity: 0,
        leadtimeValue: 0,
        leadtimeUnit: 'Days' as const,
        unitCost: 0,
        markup: null,
        unitPrice: 0,
        total: 0,
        order: result.length + 1,
        unitPriceTotal: 0,
        isQuantityEditable: false,
        isValidQuantity: false,
        showLeadTimeInputFields: false
      });
    }

    return result;
  }

  /**
   * Transform basic metadata to PartDetail format
   * Used when we only have basic draft V1 metadata (qty1, qty2, qty3, etc.)
   */
  private transformBasicMetadataToPartDetail(part: any, index: number): PartDetail {
    // Transform quantity pricing from available qty1, qty2, qty3
    const quantityPricing: QuantityPricing[] = [
      {
        quantity: part.qty1 || 0,
        leadtimeValue: 0, // Default lead time
        leadtimeUnit: 'Days' as const,
        unitCost: 0,
        markup: null,
        unitPrice: 0,
        total: 0,
        order: 1,
        unitPriceTotal: 0,
        // isLeadtimeEditing: true,
        isQuantityEditable: true,
        isValidQuantity: !!(part.qty1 && part.qty1 > 0),
        showLeadTimeInputFields: false
      },
      {
        quantity: part.qty2 || null,
        leadtimeValue: 0,
        leadtimeUnit: 'Days' as const,
        unitCost: 0,
        markup: null,
        unitPrice: 0,
        total: 0,
        order: 2,
        unitPriceTotal: 0,
        // isLeadtimeEditing: part.qty2 && part.qty2 > 0,
        isQuantityEditable: true,
        isValidQuantity: !!(part.qty2 && part.qty2 > 0),
        showLeadTimeInputFields: false
      },
      {
        quantity: part.qty3 || null,
        leadtimeValue: 0,
        leadtimeUnit: 'Days' as const,
        unitCost: 0,
        markup: null,
        unitPrice: 0,
        total: 0,
        order: 3,
        unitPriceTotal: 0,
        // isLeadtimeEditing: part.qty3 && part.qty3 > 0,
        isQuantityEditable: true,
        isValidQuantity: !!(part.qty3 && part.qty3 > 0),
        showLeadTimeInputFields: false
      }
    ];

    // Transform PDF files
    const files: PartFile[] = (part.pdf_files || []).map((file: any) => ({
      fileName: file.fileName,
      fileType: this.getFileType(file.file_url?.split('/').pop() || ''),
      fileURL: file.file_url || '',
      extractedContent: file.extracted_content || ''
    }));

    // Create part configuration with available data
    const configuration: PartConfiguration = {
      materials: part.material ? [{
        name: '',
        cost: null,
        costType: 'Per Unit' as const,
        reference: ''
      }] : [],
      tools: [],
      processes: part.process ? [{
        name: '',
        machineCost: null,
        setupCost: null,
        machineCostType: 'Per Unit' as const,
        setupCostType: 'Fixed' as const,
        reference: ''
      }] : [],
      additionalCosts: part.post_production ? [{
        name: '',
        cost: null,
        costType: 'Fixed' as const,
        reference: ''
      }] : []
    };

    return {
      partId: part.rfq_part_id || index + 1,
      partName: part.part_name || `Part ${index + 1}`,
      partNumber: part.part_number?.toString() || (index + 1).toString(),
      files,
      notesForBuyer: part.notesForBuyer || '',
      internalNotes: '',
      quantityPricing,
      configuration,
      isPartDetailCompleted: false,
      isActivePart: index === 0, // Set the first part as active by default
      extractedContent: this.extractPartContentFromBasicMetadata(part.pdf_files || []),
      isInternalNotesEnabled: false,
      isAiDataLoaded: false
    };
  }

  /**
   * Extract part content from PDF files for basic metadata
   */
  private extractPartContentFromBasicMetadata(pdfFiles: any[]): string {
    if (!Array.isArray(pdfFiles) || pdfFiles.length === 0) return '';
    
    return pdfFiles
      .map(file => file.extracted_content || '')
      .filter(content => content && content.trim() !== '')
      .join('\n\n');
  }

  /**
   * Transform part files from draft V1 data
   */
  private transformPartFilesFromDraftV1(pdfFiles: any[]): PartFile[] {
    return pdfFiles.map(file => ({
      fileName: file.fileName,
      fileType: this.getFileType(file.fileUrl?.split('/').pop() || ''),
      fileURL: file.fileUrl || '',
      extractedContent: file.extractedContent || ''
    }));
  }

  /**
   * Transform part files from V2 data using parsedQuoteDetailsFromDynamo.rfqMetaData
   */
  private transformPartFilesFromV2(rfqPartId: number): PartFile[] {
    try {
      if (!this.parsedQuoteDetailsFromDynamo?.rfqMetaData) {
        return [];
      }

      const rfqMetaData = JSON.parse(this.parsedQuoteDetailsFromDynamo.rfqMetaData);
      const partData = rfqMetaData?.find((part: any) => 
        part.rfqPartId === rfqPartId || part.rfq_part_id === rfqPartId
      );

      if (!partData) {
        return [];
      }

      const pdfFiles = partData.pdfFiles || partData.pdf_files || [];
      return pdfFiles.map((file: any) => ({
        fileName: file.fileName,
        fileType: this.getFileType(file.fileUrl?.split('/').pop() || file.file_url?.split('/').pop() || ''),
        fileURL: file.fileUrl || file.file_url || '',
        extractedContent: file.extractedContent || file.extracted_content || ''
      }));
    } catch (error) {
      console.error('Error transforming part files from V2:', error);
      return [];
    }
  }

  /**
   * Transform part details from V1 response structure
   */
  private transformPartDetailsFromV1Response(v1PartData: any[]): PartDetail[] {
    if (!Array.isArray(v1PartData) || v1PartData.length === 0) {
      return [];
    }

    let allParts: PartDetail[] = v1PartData.map((part, index) => ({
      partId: part.rfq_part_id || index + 1,
      partName: part.part_name || `Part ${index + 1}`,
      partNumber: part.part_number || (index + 1).toString(),
      files: this.transformPartFilesFromV1Response(part),
      notesForBuyer: part.notesForBuyer || part.notes_for_buyer || '',
      internalNotes: '',
      quantityPricing: this.transformQuantityPricingFromV1Response(part),
      configuration: this.transformPartConfigurationFromV1Response(part),
      isPartDetailCompleted: false,
      extractedContent: part.pdf_files?.[0]?.extracted_content || '',
      isInternalNotesEnabled: false,
      isAiDataLoaded: false,
      isValidated: false,
      isActivePart: false,
      isValid: false
    }));

    if(allParts.length > 0){
      allParts[0].isActivePart = true;
    }
    return allParts;
  }

  /**
   * Transform part files from V1 response
   */
  /**
   * Get file type based on file extension
   */
  private getFileType(fileName: string): string {
    const extension = fileName.toLowerCase().split('.').pop() || '';
    const pdfTypes = ['pdf'];
    const drawingTypes = ['dwg', 'dxf', 'slddrw'];
    const imageTypes = ['png', 'jpg', 'jpeg', 'bmp', 'tif', 'tiff'];
    const modelTypes = ['step', 'stp', 'sldprt', 'igs', 'iges', 'ipt', 'x_t', 'stl', 'obj', '3mf'];
    
    if (pdfTypes.includes(extension)) return 'Drawing2D';
    if (drawingTypes.includes(extension)) return 'Drawing2D';
    if (imageTypes.includes(extension)) return 'Drawing2D';
    if (modelTypes.includes(extension)) return 'Model3D';
    
    return 'Drawing2D'; // Default
  }

  private transformPartFilesFromV1Response(part: any): PartFile[] {
    if (!part.pdf_files || !Array.isArray(part.pdf_files)) {
      return [];
    }

    return part.pdf_files.map((file: any) => ({
      fileName: file.file_url,
      fileType: this.getFileType(file.file_url),
      fileURL: file.file_url || '',
      extractedContent: file.extracted_content || ''
    }));
  }

  /**
   * Transform quantity pricing from V1 response
   */
  private transformQuantityPricingFromV1Response(part: any): QuantityPricing[] {
    const quantities = [];
    
    if (part.qty1 && part.qty1 > 0) {
      quantities.push({
        quantity: part.qty1,
        leadtimeValue: 0,
        leadtimeUnit: 'Days' as const,
        unitCost: 0,
        markup: null,
        unitPrice: 0,
        total: 0,
        order: 1,
        unitPriceTotal: 0,
        isQuantityEditable: true,
        isValidQuantity: true,
        showLeadTimeInputFields: false
      });
    }
    
    if (part.qty2 && part.qty2 > 0) {
      quantities.push({
        quantity: part.qty2,
        leadtimeValue: 0,
        leadtimeUnit: 'Days' as const,
        unitCost: 0,
        markup: null,
        unitPrice: 0,
        total: 0,
        order: 2,
        unitPriceTotal: 0,
        isQuantityEditable: true,
        isValidQuantity: true,
        showLeadTimeInputFields: false
      });
    }
    
    if (part.qty3 && part.qty3 > 0) {
      quantities.push({
        quantity: part.qty3,
        leadtimeValue: 0,
        leadtimeUnit: 'Days' as const,
        unitCost: 0,
        markup: null,
        unitPrice: 0,
        total: 0,
        order: 3,
        unitPriceTotal: 0,
        isQuantityEditable: true,
        isValidQuantity: true,
        showLeadTimeInputFields: false
      });
    }
    while (quantities.length < 3) {
      quantities.push({
        quantity: 0,
        leadtimeValue: 0,
        leadtimeUnit: 'Days' as const,
        unitCost: 0,
        markup: null,
        unitPrice: 0,
        total: 0,
        order: quantities.length + 1,
        unitPriceTotal: 0,
        isQuantityEditable: false,
        isValidQuantity: false,
        showLeadTimeInputFields: false
      });
    }

    return quantities;
  }

  /**
   * Transform part configuration from V1 response
   */
  private transformPartConfigurationFromV1Response(part: any): PartConfiguration {
    return {
      materials: [
        { name: '', cost: null, costType: 'Per Unit' as 'Per Unit', reference: '' }
      ],
      tools: [
        { name: '', cost: null, costType: 'Fixed' as 'Fixed', reference: '' }
      ],
      processes: [
        { name: '', machineCost: null, setupCost: null, machineCostType: 'Fixed' as 'Fixed', setupCostType: 'Fixed' as 'Fixed', reference: '' }
      ],
      additionalCosts: [
        { name: 'Inspection', cost: null, costType: 'Fixed' as 'Per Unit', reference: '' },
        { name: 'Overhead', cost: null, costType: 'Fixed' as 'Per Unit', reference: '' }
      ]
    };
  }

  /**
   * Transform quantity pricing from draft V1 data
   */
  private transformQuantityPricingFromDraftV1(partQuantity: any[]): QuantityPricing[] {
    return partQuantity.map((qty, index) => ({
      order: qty.order || index + 1,
      quantity: qty.qty || 0,
      leadtimeValue: this.parseLeadTime(qty.summaryData?.leadTime),
      leadtimeUnit: 'Days' as const,
      unitCost: 0,
      markup: 0,
      unitPrice: this.parsePrice(qty.summaryData?.pricePerUnit?.totalPrice),
      total: qty.totalCost || 0,
      unitPriceTotal: this.parsePrice(qty.summaryData?.pricePerUnit?.totalPrice),
      isLeadtimeEditing: this.getIntegrationType() !== 'MFG' ? true : qty.qty > 0 ? true : false,
      isQuantityEditable: true,
      isValidQuantity: true,
      showLeadTimeInputFields: true
    }));
  }

  /**
   * Transform part configuration from draft V1 data
   */
  private transformPartConfiguration(quoteAssistModel: any): PartConfiguration {
    return {
      materials: this.transformMaterials(quoteAssistModel.materials),
      tools: this.transformTools(quoteAssistModel.tooling),
      processes: this.transformProcesses(quoteAssistModel.manufacturingProcess),
      additionalCosts: this.transformAdditionalCosts(quoteAssistModel.additionalRequirements)
    };
  }

  /**
   * Transform materials from draft V1 data
   */
  private transformMaterials(materialsModel: any): MaterialItem[] {
    if (!materialsModel?.materialsList) return [];
    
    return materialsModel.materialsList.map((material: any) => ({
      name: material.name || '',
      cost: this.parsePrice(material.totalMaterialCost),
      costType: 'Per Unit' as const,
      reference: ''
    }));
  }

  /**
   * Transform tools from draft V1 data
   */
  private transformTools(toolingModel: any): ToolItem[] {
    if (!toolingModel?.toolingList) return [];
    
    return toolingModel.toolingList.map((tool: any) => ({
      name: tool.toolName || '',
      cost: this.parsePrice(tool.estimatedCost),
      costType: 'Fixed' as const,
      reference: ''
    }));
  }

  /**
   * Transform processes from draft V1 data
   */
  private transformProcesses(manufacturingModel: any): ProcessItem[] {
    if (!manufacturingModel?.manufacturingList) return [];
    
    return manufacturingModel.manufacturingList.map((process: any) => ({
      name: process.processName || '',
      machineCost: this.parsePrice(process.totalMachiningCost),
      setupCost: null,
      machineCostType: 'Per Unit' as const,
      setupCostType: 'Fixed' as const,
      reference: ''
    }));
  }

  /**
   * Transform additional costs from draft V1 data
   */
  private transformAdditionalCosts(additionalModel: any): AdditionalCostItem[] {
    if (!additionalModel?.additionalRequirementList) return [];
    
    return additionalModel.additionalRequirementList.map((item: any) => ({
      name: item.processName || '',
      cost: this.parsePrice(item.estimatedCost),
      costType: 'Fixed' as const,
      reference: ''
    }));
  }

  /**
   * Transform cost breakdown from draft V1 data
   * Handles both complete draft V1 data and basic metadata
   */
  private transformCostBreakdown(rfqPartList: any[]): CostBreakdown {
    if (!Array.isArray(rfqPartList) || rfqPartList.length === 0) {
      return this.createDefaultCostBreakdown();
    }

    // Check if this is basic metadata (has qty1, qty2, qty3) or complete draft V1 data
    const isBasicMetadata = rfqPartList[0] && rfqPartList[0].hasOwnProperty('qty1') && rfqPartList[0].hasOwnProperty('qty2') && rfqPartList[0].hasOwnProperty('qty3');
    if (isBasicMetadata) {
      return this.createDefaultCostBreakdown();
    }

    // Handle complete draft V1 data case - aggregate costs from all parts
    let totalMaterialsCost = 0;
    let totalToolingCost = 0;
    let totalManufacturingCost = 0;
    let totalLaborCost = 0;
    let totalOverheadCost = 0;
    let totalOtherCosts = 0;
    let totalProfit = 0;

    rfqPartList.forEach(part => {
      const quoteAssistData = part.quoteAssistData || {};
      const quoteAssistModel = quoteAssistData.quoteAssistModel || {};
      
      // Materials
      if (quoteAssistModel.materials?.materialsList) {
        quoteAssistModel.materials.materialsList.forEach((material: any) => {
          totalMaterialsCost += this.parsePrice(material.totalMaterialCost) || 0;
        });
      }
      
      // Tooling
      if (quoteAssistModel.tooling?.toolingList) {
        quoteAssistModel.tooling.toolingList.forEach((tool: any) => {
          totalToolingCost += this.parsePrice(tool.estimatedCost) || 0;
        });
      }
      
      // Manufacturing
      if (quoteAssistModel.manufacturingProcess?.manufacturingList) {
        quoteAssistModel.manufacturingProcess.manufacturingList.forEach((process: any) => {
          totalManufacturingCost += this.parsePrice(process.totalMachiningCost) || 0;
        });
      }
      
      // Labor
      if (quoteAssistModel.labor?.laborList) {
        quoteAssistModel.labor.laborList.forEach((labor: any) => {
          totalLaborCost += this.parsePrice(labor.estimatedCost) || 0;
        });
      }
      
      // Overhead
      if (quoteAssistModel.overhead?.overheadList) {
        quoteAssistModel.overhead.overheadList.forEach((overhead: any) => {
          totalOverheadCost += this.parsePrice(overhead.estimatedCost) || 0;
        });
      }
      
      // Other costs
      if (quoteAssistModel.otherCosts?.otherCostList) {
        quoteAssistModel.otherCosts.otherCostList.forEach((cost: any) => {
          totalOtherCosts += this.parsePrice(cost.estimatedCost) || 0;
        });
      }
      
      // Profit
      if (quoteAssistModel.profit?.totalPrice) {
        totalProfit += this.parsePrice(quoteAssistModel.profit.totalPrice) || 0;
      }
    });

    return {
      materials: {
        totalCost: totalMaterialsCost,
        items: this.extractCostItems(rfqPartList, 'materials')
      },
      tooling: {
        totalCost: totalToolingCost,
        items: this.extractCostItems(rfqPartList, 'tooling')
      },
      manufacturing: {
        totalCost: totalManufacturingCost,
        items: this.extractCostItems(rfqPartList, 'manufacturingProcess')
      },
      labor: {
        totalCost: totalLaborCost,
        items: this.extractCostItems(rfqPartList, 'labor')
      },
      overhead: {
        totalCost: totalOverheadCost,
        items: this.extractCostItems(rfqPartList, 'overhead')
      },
      otherCosts: {
        totalCost: totalOtherCosts,
        items: this.extractCostItems(rfqPartList, 'otherCosts')
      },
      profit: {
        profitMargin: 30, // Default profit margin
        totalProfit: totalProfit
      }
    };
  }

  /**
   * Create default cost breakdown structure with 0 costs
   * Used when we only have basic metadata (no cost estimates available)
   */
  private createDefaultCostBreakdown(): CostBreakdown {
    return {
      materials: {
        totalCost: 0,
        items: []
      },
      tooling: {
        totalCost: 0,
        items: []
      },
      manufacturing: {
        totalCost: 0,
        items: []
      },
      labor: {
        totalCost: 0,
        items: []
      },
      overhead: {
        totalCost: 0,
        items: []
      },
      otherCosts: {
        totalCost: 0,
        items: []
      },
      profit: {
        profitMargin: 15, // Default profit margin
        totalProfit: 0
      }
    };
  }

  /**
   * Extract cost items from a specific section
   */
  private extractCostItems(rfqPartList: any[], sectionName: string): CostItem[] {
    const items: CostItem[] = [];
    
    rfqPartList.forEach(part => {
      const quoteAssistModel = part.quoteAssistData?.quoteAssistModel || {};
      const section = quoteAssistModel[sectionName];
      
      if (section) {
        let listKey = '';
        let nameKey = '';
        let costKey = '';
        
        switch (sectionName) {
          case 'materials':
            listKey = 'materialsList';
            nameKey = 'name';
            costKey = 'totalMaterialCost';
            break;
          case 'tooling':
            listKey = 'toolingList';
            nameKey = 'toolName';
            costKey = 'estimatedCost';
            break;
          case 'manufacturingProcess':
            listKey = 'manufacturingList';
            nameKey = 'processName';
            costKey = 'totalMachiningCost';
            break;
          case 'labor':
            listKey = 'laborList';
            nameKey = 'processName';
            costKey = 'estimatedCost';
            break;
          case 'overhead':
            listKey = 'overheadList';
            nameKey = 'overheadTitle';
            costKey = 'estimatedCost';
            break;
          case 'otherCosts':
            listKey = 'otherCostList';
            nameKey = 'processName';
            costKey = 'estimatedCost';
            break;
        }
        
        if (section[listKey]) {
          section[listKey].forEach((item: any) => {
            items.push({
              name: item[nameKey] || '',
              cost: this.parsePrice(item[costKey]) || 0,
              description: item.purpose || item.description || ''
            });
          });
        }
      }
    });
    
    return items;
  }

  /**
   * Extract part content from PDF files
   */
  private extractPartContent(pdfFiles: any[]): string {
    if (!pdfFiles || pdfFiles.length === 0) return '';
    
    return pdfFiles
      .map(file => file.extractedContent || '')
      .filter(content => content && content.trim() !== '')
      .join('\n\n');
  }

  /**
   * Parse price string to number
   */
  private parsePrice(price: any): number {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      const parsed = parseFloat(price.replace(/[^\d.-]/g, ''));
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  /**
   * Parse lead time string to number
   */
  private parseLeadTime(leadTime: any): number {
    if (typeof leadTime === 'number') return leadTime;
    if (typeof leadTime === 'string') {
      const parsed = parseInt(leadTime.replace(/[^\d]/g, ''));
      return isNaN(parsed) ? 90 : parsed; // Default to 90 days
    }
    return 90;
  }
  
  private handleBackendError(stepId: string, error: any): void {
    this.handleError(`Backend Operation - ${stepId}`, error, `Failed to save ${stepId} data`);
  }
  
  private updateDataWithBackendResponse(stepId: string, response: any): void {
    this.handleBackendResponse(stepId, response);
    this.saveProgress();
  }
  
  private handleNavigationError(error: any): void {
    this.loadingService.hideLoading();
    this.handleError('Navigation', error, 'Failed to proceed to next step');
  }
  
  // ===== PUBLIC METHODS =====
  
  openFileUploadModal(partIndex?: number): void {
    this.currentFileUploadPartIndex = partIndex;
    this.showFileUploadModal = true;
  }
  
  closeFileUploadModal(): void {
    this.showFileUploadModal = false;
    this.currentFileUploadPartIndex = undefined;
  }

  clearErrors(): void {
    this._errorState$.next(this.initializeErrorState());
  }

  /**
   * Skip quote assist (following V1 pattern)
   */
  skipQuoteAssist(): void {
    // Mark all steps as completed
    this._progressState$.next({
      ...this._progressState$.value,
      steps: this._progressState$.value.steps.map(step => ({
        ...step,
        status: 'completed' as const
      })),
      overallProgress: 100,
      canProceed: true
    });
    
    // Save progress
    this.saveData().subscribe({
      next: (rfqId) => {
        this.navigateToStep('quote-summary');
      },
      error: (error) => {
        this.navigateToStep('quote-summary');
      }
    });
  }

  private formatLeadTime(value: number, unit: 'Days' | 'Weeks'): string {
    let totalDays = value;
    if (unit === 'Weeks') {
      totalDays = value * 7;
    }
    
    return totalDays.toString();
  }

  /**
   * Get integration type with proper type safety
   */
  getIntegrationType(): 'MFG' | 'MFGDirect' | 'SUW' {
    const integration = this.queryParams.integration;
    if (integration === 'MFG' || integration === 'MFGDirect' || integration === 'SUW') {
      return integration;
    }
    return 'MFG'; // Default fallback
  }

  /**
   * Expose debug methods to console for testing
   */
  private exposeDebugMethods(): void {
    (window as any).debugQuoteAssistV2 = {
      getProgressState: () => this._progressState$.value,
      getValidationState: () => this._validationState$.value,
      getErrorState: () => this._errorState$.value,
      getQuoteData: () => this.quoteData,
      getQueryParams: () => this.queryParams,
      saveProgress: () => this.saveProgress(),
      saveDraftData: (context?: string) => this.saveDraftData(context),
      submitQuote: () => this.submitQuote(),
      logEvent: (event: string, data?: any) => this.logEvent(event, data),
      getSurveyDoneStatus: () => this.queryParams.surveyDone,
      simulateSurveyDone: () => {
        this.queryParams.surveyDone = true;
        this.cdr.detectChanges();
      }
    };
    
    console.log('[Quote-Assist-V2] DEBUG: Debug methods exposed to window.debugQuoteAssistV2');
  }

  /**
   * Check if any part is completed to enable draft saving
   */
  isAnyPartCompleted(): boolean {
    if (!this.quoteData?.partDetails || this.quoteData.partDetails.length === 0) {
      return false;
    }
    
    return this.quoteData.partDetails.some(part => part.isPartDetailCompleted === true);
  }

  private getMixPanelEventForIntegrationDraft(integrationType: 'MFG' | 'MFGDirect' | 'SUW'): string {
    switch (integrationType) {
      case 'MFG':
        return MixPanelEvents.SaveToDraftMarketPlace;
      case 'MFGDirect':
        return MixPanelEvents.SaveToDraftSelfCreated;
      case 'SUW':
        return MixPanelEvents.SaveToDraftWebsiteSubmitted;
      default:
        return MixPanelEvents.SaveToDraftSelfCreated;
    }
  }

}
